/**
 * Advisor portal UI strings (EN + ES). Merged into i18n under key `advisorPortal`.
 * Use: t('advisorPortal.someKey')
 */

export const advisorPortalEn = {
  activeCasesTitle: 'Active Cases',
  activeCasesSubtitle: 'Open applications assigned to your team.',
  customerSearchTitle: 'Customer Search',
  customerSearchSubtitle: 'Find customers by name, mobile, email, or ID',
  refreshTitle: 'Refresh list',
  tabActiveCases: 'Active Cases',
  tabCustomerSearch: 'Customer Search',
  addCustomer: 'Add Customer',
  showingLabel: 'Showing:',
  myApplications: 'My Applications',
  specificAdvisor: 'Specific Advisor',
  allApplications: 'All Applications',
  assignedTo: 'Assigned to',
  advisorNumber: 'Advisor #{{id}}',
  searchPlaceholderActive: 'Search by name, mobile, email, customer ID, case ID...',
  filterStatusLabel: 'Status',
  filterSortLabel: 'Sort by',
  filterQuickLabel: 'Quick filters',
  compactFiltersBarLabel: 'Active case filters (bottom bar)',
  compactSearchPh: 'Search…',
  allStatuses: 'All Statuses',
  statusNew: 'New',
  statusInProgress: 'In Progress',
  statusPendingReview: 'Pending Review',
  statusWaitingOnClient: 'Waiting on Client',
  statusWaitingOnDocuments: 'Waiting on Documents',
  statusReadyToSubmit: 'Ready to Submit',
  statusSubmitted: 'Submitted',
  statusInReviewGovernment: 'In Review',
  statusApproved: 'Approved',
  statusRejected: 'Rejected',
  statusCancelledClient: 'Cancelled (Client)',
  statusCancelledInternal: 'Cancelled (Internal)',
  statusExpiredAbandoned: 'Expired / Abandoned',
  statusCompleted: 'Completed',
  statusArchived: 'Archived',
  allCaseTypes: 'All Case Types',
  caseTypeDNV: 'Digital Nomad (DNV)',
  caseTypeASO: 'Arraigo Social (ASO)',
  caseTypeALA: 'Arraigo Laboral (ALA)',
  caseTypeAFF: 'Arraigo Familiar (AFF)',
  caseTypeEUF: 'EU Family (EUF)',
  caseTypeTFR: 'Family Reunion (TFR)',
  caseTypeINV: 'Golden Visa (INV)',
  caseTypeSTU: 'Student (STU)',
  caseTypeNLV: 'Non-Lucrative (NLV)',
  caseTypeHQP: 'Highly Qualified (HQP)',
  caseTypeSTA: 'Startup (STA)',
  caseTypeRAE: 'Self-Employed (RAE)',
  allAdvisors: 'All Advisors',
  unassigned: 'Unassigned',
  needsAttention: 'Needs Attention',
  sortStatusDateNewest: 'Status date: Newest first',
  sortStatusDateOldest: 'Status date: Oldest first',
  casesCount: '{{count}} cases',
  casesCountOne: '1 case',
  customersCount: '{{count}} customers',
  customersCountOne: '1 customer',
  clearAll: 'Clear all',
  loadingCases: 'Loading cases...',
  loadingCustomers: 'Loading customers...',
  dirSearchPlaceholder: 'Search by name, email, mobile, customer ID...',
  dirAll: 'All',
  dirFollowUp: 'Follow Up',
  dirRenewal: 'Renewal Due',
  dirInquiry: 'Inquiry',
  deleteUserFailed: 'Failed to delete user. Please try again.',
  tableCustomer: 'Customer',
  tableFollowUp: 'Follow Up',
  tableCaseType: 'Case Type',
  tableContact: 'Mobile / Email',
  tableStatus: 'Status',
  tableStatusDate: 'Status Date',
  tableAdvisor: 'Advisor',
  tableDocs: 'Docs',
  tableOpen: 'Open',
  noApplicationsFound: 'No applications found',
  familySuffix: 'family',
  familyLine: '{{name}} family',
  defaultFamilyName: 'Family',
  defaultCaseType: 'Regularization 2026',
  brandSubtitle: 'Advisor Portal',
  roleAdvisor: 'Advisor',
  menuShowAdvisors: 'Show all advisors',
  menuPortalSettings: 'Roles & default cases',
  menuCustomerSignupLink: 'Customer signup link',
  menuLogOut: 'Log out',
  menuAriaLabel: 'Advisor menu, {{name}}',
  workflowNew: 'New',
  workflowInProgress: 'In Progress',
  workflowReadyToApprove: 'Ready to Approve',
  workflowCompleted: 'Completed',
  workflowArchived: 'Archived',

  /* Customer directory */
  dirNoCustomers: 'No customers found',
  dirHeaderCustomer: 'Customer',
  dirHeaderContact: 'Mobile / Email',
  dirHeaderFollowUp: 'Follow Up',
  dirHeaderAddedOn: 'Added on',
  dirHeaderLastActivity: 'Last Activity',
  followUpBadge: 'Follow-up',
  dirOpen: 'Open',
  dirDelete: 'Delete',
  dirDeleting: 'Deleting',
  dirUserNumber: 'User #{{id}}',
  dirFamilyLine: '{{name}} family',

  /* Case detail - tabs & chrome */
  tabCaseDetails: 'Details',
  tabHistory: 'History',
  tabDocuments: 'Documents',
  tabDocumentsTooltip:
    'Sections with at least one upload. Fully approved (every file in the section accepted): {{approved}}/{{total}}.',
  loadingCaseDetails: 'Loading case details...',
  caseDetailsCardTitle: 'Case Details',
  caseNumber: 'Case Number',
  caseType: 'Case Type',
  assignedAdvisor: 'Assigned Advisor',
  caseStatus: 'Status',
  generateForm: 'Generate Form',
  downloadPackage: 'Download Package',
  draftBanner: 'DRAFT - This application has not been submitted.',
  recentHistory: 'Recent history',
  addNoteTitle: 'Add Note',
  viewAll: 'View all',
  noHistoryYet: 'No history yet.',
  documentsCardTitle: 'Documents',
  manage: 'Manage',
  documentsProgress: '{{up}} of {{total}} with uploads · {{ap}} of {{total}} fully approved',
  docRowPending: 'Pending',
  fileFallback: 'File',
  activityPageTitle: 'History',
  activityFilterAll: 'All',
  activityFilterNotes: 'Notes',
  activityFilterActivity: 'Activity',

  /* Document category cards (advisor) */
  docCat_passport_name: 'Passport',
  docCat_passport_desc: 'Full passport - all pages. Expired passports accepted.',
  docCat_proof_of_residency_name: 'Proof of residency',
  docCat_proof_of_residency_desc: 'Documents showing continuous presence in Spain.',
  docCat_criminal_record_name: 'Criminal record',
  docCat_criminal_record_desc: 'Antecedentes penales - apostilled & translated, less than 3 months old.',
  docCat_asylum_receipt_name: 'Asylum receipt',
  docCat_asylum_receipt_desc: 'Resguardo de asilo - filed before 1 Jan 2026.',
  docCat_asylum_receipt_note: 'Must be filed before 1 January 2026',
  docCat_other_name: 'Other',
  docCat_other_desc: 'Additional supporting documents.',

  /* Sidebar document slot status */
  sidebarNone: 'None',
  sidebarAwaiting: 'Awaiting',
  sidebarUploadedN: '{{count}} uploaded',
  sidebarComplete: 'Complete',
  sidebarRejected: 'Rejected',
  sidebarPending: 'Pending',

  /* Review status labels */
  reviewAccepted: 'Accepted',
  reviewRejected: 'Rejected',
  reviewPending: 'Pending',
  reviewAwaitingUpload: 'Awaiting upload',
  reviewAwaiting: 'Awaiting',
  reviewOptionApproved: 'Approved',

  asylumBefore: 'Submitted before 1 Jan 2026',
  asylumAfter: 'Submitted on or after 1 Jan 2026',
  asylumNever: 'Never submitted',

  caseSource: 'Source',
  caseCreatedAt: 'Created At',
  caseSourceInPerson: 'In-Person',
  breadcrumbBack: 'Back',
  breadcrumbApplications: 'Applications',
  loadingCaseShort: 'Loading case…',
  invalidCaseId: 'Invalid or unknown case ID.',
  postedToHistory: 'Posted to history',
  couldNotPostNote: 'Could not post note',

  docBadgeAwaitingUpload: 'Awaiting Upload',
  docBadgePendingReview: 'Pending Review',
  docBadgeAccepted: 'Accepted',
  docBadgeRejected: 'Rejected',
  docUploadCount: '{{count}} docs',
  docFileExtFallback: 'FILE',
  toastDownloadFailed: 'Could not download file',
  toastMissingSession: 'Missing session - reload the case page',
  toastNoFile: 'No file selected',
  toastUploaded: '{{name}} uploaded',
  toastUploadFailed: 'Upload failed',
  toastRestoreBeforeStatus: 'Restore the file before changing review status',
  toastStatusUpdated: 'Status updated',
  toastUpdateFailed: 'Update failed',
  toastEnterRejectReason: 'Enter a rejection reason',
  toastDocumentRejected: 'Document rejected',
  toastRejectFailed: 'Failed to reject',
  toastPreviewLoadFailed: 'Could not load preview.',
  toastFileRestored: 'File restored',
  toastRestoreFailed: 'Restore failed',
  toastFileRemoved: 'File removed',
  toastFileArchived: 'File archived',
  toastRequestFailed: 'Request failed',
  previewCloseAria: 'Close preview',
  previewLoading: 'Loading preview…',
  previewEmptyFile: '(empty file)',
  previewUnsupportedHint:
    'In-browser preview is not available for this file type. Use Download to open it on your device.',
  previewClose: 'Close',
  docDeleteOrArchiveAria: 'Archive or delete file',
  docDeleteFileAria: 'Delete file',
  docLabelDocument: 'Document',
  docSectionUploaded: 'Uploaded',
  docLabelNew: 'New',
  docPlaceholderRejectReason: 'Reason for rejection',
  docConfirmRejection: 'Confirm rejection',
  docCancel: 'Cancel',
  docUploadReplacement: 'Upload replacement',
  docUploadFile: 'Upload file',
  docOr: 'or',
  docScanDocument: 'Scan document',
  docTypesHint: 'PDF, JPG, PNG, HEIC → stored as PDF',
  docLabelDocumentType: 'Document type',
  docSelectType: 'Select type',
  docFrom: 'From',
  docTo: 'To',
  docShortDescLabel: 'Short description (optional, max 100 characters)',
  docShortDescPlaceholder: 'e.g. prior employment contract',
  docDropBrowse: 'Drop file here or browse',
  docUploading: 'Uploading…',
  docDeletePermanent: 'Delete permanently',
  docMoveArchive: 'Move to archive',
  docDeleteModalTitlePermanent: 'Permanently delete this file?',
  docDeleteModalTitleArchive: 'Move this file to the archive?',
  docDeleteModalBodyPermanent:
    'This removes the file and its storage copy. You cannot undo this action.',
  docDeleteModalBodyArchive:
    'The file will disappear from the checklist but stay in the Archived section below, where you can restore it or delete it permanently.',
  docAddDocument: 'Add document',
  docArchivedTitle: 'Archived',
  docArchivedFileCount: '{{count}} file(s)',
  docArchivedHelp:
    'Restore to return a file to the checklist, or delete permanently to remove it from storage.',
  docRestore: 'Restore',
  docFooterSummaryBase: '{{sections}} required sections · {{active}} active',
  docFooterArchivedSuffix: ' · {{count}} archived',
  docArchivedBadge: 'Archived',
  docClosePanelAria: 'Close document panel',
  docPreviewHint: 'Open preview to view PDFs and images in the browser without downloading.',
  docPreview: 'Preview',
  docDownload: 'Download',
  docReviewStatus: 'Review status',
  docDrawerPendingReview: 'Pending Review',
  docDrawerAccepted: 'Accepted',
  docDrawerRejected: 'Rejected',
  docDateFromLabel: 'Date from',
  docDateToLabel: 'Date to',
  docDescription: 'Description',

  sectionIdentity: 'Identity',
  identityDocumentsTitle: 'Identity documents',
  identityPassportLabel: 'Passport',
  identityOtherIdLabel: 'Other ID',
  identityStep1: 'Step 1 - Enter details',
  identityStep2: 'Step 2 - Upload the document',
  identityDocumentType: 'Document type',
  identitySelect: 'Select...',
  identityNationalId: 'National ID',
  identityTravelDocument: 'Travel Document',
  identityCedula: 'Cédula de Inscripción',
  identityIdNumber: 'ID number',
  identityFullNamePassport: 'Full name (as on passport)',
  identityDocTypeLabel: 'Identity document',
  sexMale: 'Male',
  sexFemale: 'Female',
  sexUndetermined: 'Undetermined',
  oarCaseNumber: 'OAR case number',
  otherAsylumDocuments: 'Other asylum documents',
  changeFile: 'Change',
  sectionPersonalInformation: 'Personal Information',
  sectionContact: 'Contact',
  sectionAddress: 'Address',
  sectionFamily: 'Family',
  sectionEngagementConsent: 'Engagement & Consent',
  sectionRepresentative: 'Representative',
  sectionNotificationAddress: 'Notification Address',
  pathwayAndScreening: 'Pathway & Screening',
  pathwayAppliedAsylumBefore2026: 'Applied for asylum before 1 Jan 2026',
  pathwayApplyingFor: 'Applying for',
  pathwayHasPassport: 'Has passport',
  pathwayCriminalRecord: 'Criminal record',
  pathwayPriorApplicationsSpain: 'Prior applications in Spain',
  pathwayWork: 'Irregular Situation (Work)',
  pathwayFamily: 'Irregular Situation (Family)',
  pathwayVulnerability: 'Vulnerability',
  subsectionIdentity: 'Identity',
  subsectionOther: 'Other',
  familyFather: 'Father',
  familyMother: 'Mother',
  familyParents: 'Parents',
  familySpouse: 'Spouse',
  familyNoSpouse: 'No spouse',
  familyChildrenCount: 'Children ({{count}})',
  familyChildrenPresent: 'Children listed',
  familyNoChildren: 'No children',
  familyDependentParentsCount: 'Dependent Parents ({{count}})',
  familyDependentParentsPresent: 'Dependent parents listed',
  familyNoDependentParents: 'No dependent parents',
  familyHasSpouse: 'Has spouse',
  familyHasChildren: 'Has children',
  familyHasDependentParents: 'Has dependent parents',
  engagementConsent: 'Engagement consent',
  dataProcessingConsent: 'Data processing consent',
  noReturnCommitment: 'No-return commitment',
  noHealthcareCoverage: 'No healthcare coverage',
  signature: 'Signature',
  applicantRequestedQuestions: 'Applicant requested questions before signing',
  unknownValue: 'Unknown',
  yes: 'Yes',
  no: 'No',
  approve: 'Approve',
  caseApproved: 'Case approved',
  caseApproveFailed: 'Could not approve case',
  caseTypeUpdated: 'Case type updated',
  caseTypeUpdateFailed: 'Could not update case type',
  editCaseTypeAria: 'Edit case type',
  sectionEdit: 'Edit',
  sectionSave: 'Save',
  sectionSaving: 'Saving...',
  sectionCancel: 'Cancel',
  referralCode: 'Referral Code',
  referralPlaceholder: 'e.g. R74321',
  referralLookup: 'Look up',
  referralSelfError: 'The applicant cannot be their own referrer.',
  referralCodeNotFound: 'Code not found',
  referralNone: 'None',
  editReferralAria: 'Edit referral',
  addReferralAria: 'Add referral',
  editPriorAsylumAria: 'Edit prior asylum',
  priorAsylum: 'Prior Asylum',
  lastUpdated: 'Last Updated',
  historyLoading: 'Loading history…',
  historyEmpty: 'No history events yet.',
  qualificationTagNote: 'Note',
  qualificationFlow: 'Flow',
  qualificationOutcome: 'Outcome',
  qualificationQuestionFallback: 'Question',
  qualificationAnswer: 'Answer',
  qualificationAnswerYes: 'Yes',
  qualificationAnswerNo: 'No',
  qualificationTitle: 'Applicant registration answers',
  historyBy: 'by {{name}}',
  savePostalCode5: 'Postal code must be 5 digits',
  saveRepresentativePostalCode5: 'Representative postal code must be 5 digits',
  saveNotificationPostalCode5: 'Notification postal code must be 5 digits',
  validationFirstName: 'First name must be 1-40 letters (no digits)',
  validationLastName: 'Last name must be 1-40 letters (no digits)',
  validationSecondSurname: 'Second surname must be up to 40 letters',
  validationEmail: 'Enter a valid email (6-100 chars)',
  validationPhone: 'Phone must contain 6-15 digits',
  validationDobAdult: 'Date of birth must be valid and applicant must be at least 18',
  validationPlaceOfBirth: 'Place of birth must be 1-40 letters',
  validationFatherName: "Father's name must be up to 80 letters",
  validationMotherName: "Mother's name must be up to 80 letters",
  validationPassport: 'Passport number must be 5-20 letters or digits',
  validationNie: 'NIE must match X/Y/Z + 7 digits + letter',
  validationStreet: 'Street must be 1-100 valid characters',
  validationStreetNumber: 'Street number must be 1-10 letters/digits',
  validationCity: 'City must be 1-40 letters',
  validationPostalCode: 'Postal code must be exactly 5 digits',
  validationRepresentativePhone: 'Representative phone must contain 6-15 digits',
  validationRepresentativeEmail: 'Representative email must be valid',
  validationNotificationPhone: 'Notification phone must contain 6-15 digits',
  validationNotificationEmail: 'Notification email must be valid',
  validationAuthorizedName: 'Authorized contact name must be up to 40 letters',
  validationAuthorizedPhone: 'Authorized contact phone must contain 6-15 digits',
  validationAuthorizedEmail: 'Authorized contact email must be valid',
  dobInputHint: '',
  nieInputPlaceholder: 'e.g. X1234567L',
  saveSectionFailed: 'Failed to save section',
  saveSectionDone: 'Section saved',
  savePartial: 'Saved {{count}}. Failed: {{reason}}',
  referralSaved: 'Referral saved',
  referralRemoved: 'Referral removed',
  referralSaveFailed: 'Could not save referral',
  referralRemoveFailed: 'Could not remove referral',
  genericSaved: 'Saved',
  genericCouldNotSave: 'Could not save',
  needsAttentionMarked: 'Case marked as needs attention',
  needsAttentionCleared: 'Removed from needs attention',
  needsAttentionUpdateFailed: 'Could not update flag',
  caseLabelFallback: 'Case',
  needsAttentionTitleClear: 'Clear needs-attention flag',
  needsAttentionTitleMark: 'Mark this case for follow-up',
  needsAttentionSaving: 'Saving…',
  needsAttentionLabel: 'Needs attention',
  needsAttentionMarkLabel: 'Mark needs attention',
  downloadStarted: 'Download started - saving {{name}}. Check your Downloads folder.',
  packageNameFallback: 'package',
  familyApplications: 'Applications',
  familySuffixTitle: 'Family',
  familyOverview: 'Family group overview and case history',
  familyBackToSearch: 'Back to Search',
  familyCaseHistory: 'Case History',
  familyNoLinkedMembers: 'No linked members.',
  familyMemberName: 'Name',
  familyMemberPhone: 'Phone',
  familyMemberEmail: 'Email',
  familyMemberOpen: 'Open',
  familyMembersCount: 'Members',
  familyWithEmail: 'With Email',
  familyWithMobile: 'With Mobile',
  familyCaseMember: 'Member',
  familyCaseCase: 'Case',
  familyCaseStatus: 'Status',
  familyCaseUpdated: 'Updated',
  familyNoCaseHistory: 'No case history found.',
  remove: 'Remove',
  fieldFirstName: 'First Name',
  fieldLastName: 'Last Name',
  fieldSecondSurname: 'Second Surname',
  fieldDateOfBirth: 'Date of Birth',
  fieldArrivalInSpain: 'Arrival in Spain',
  fieldPlaceOfBirth: 'Place of Birth',
  fieldCountryOfBirth: 'Country of Birth',
  fieldNationality: 'Nationality',
  fieldCivilStatus: 'Civil Status',
  fieldFatherName: "Father's Name",
  fieldMotherName: "Mother's Name",
  fieldPassportNumber: 'Passport Number',
  fieldSex: 'Sex',
  fieldMobile: 'Mobile',
  fieldEmail: 'Email',
  fieldChannel: 'Channel',
  fieldPreferredContact: 'Preferred Contact',
  fieldPreferredLanguage: 'Preferred Language',
  fieldStreet: 'Street',
  fieldStreetAddress: 'Street Name',
  fieldNumber: 'Number',
  fieldFloor: 'Floor',
  fieldDoor: 'Unit (Puerta)',
  fieldLocality: 'Locality',
  fieldCity: 'City',
  fieldProvince: 'Province',
  fieldPostalCode: 'Postal Code',
  fieldCountry: 'Country',
  fieldResidentSince: 'Resident Since',
  fieldCompany: 'Company',
  fieldFirmName: 'Firm Name',
  fieldRepresentativeId: 'Representative ID / Doc',
  fieldDniNieCif: 'DNI/NIE/CIF',
  fieldAddress: 'Address',
  fieldPhone: 'Phone',
  fieldName: 'Name',
  fieldDocumentId: 'Document ID',
  fieldElectronicNotifications: 'Electronic notifications',

  /* Add customer (/customer/new) */
  newCustomerCrumbSearch: 'Customer Search',
  newCustomerTitle: 'Add Customer',
  newCustomerSubtitle: 'Create a new customer record.',
  newCustomerBackSearch: 'Back to Search',
  newCustomerSave: 'Save Customer',
  newCustomerSaving: 'Saving...',
  newCustomerSaved: 'Saved!',
  newCustomerCancel: 'Cancel',
  newCustomerCreateFailed: 'Failed to create customer',
  newCustomerSecPersonal: 'Personal Information',
  newCustomerSecContact: 'Contact Details',
  newCustomerSecAuthorized: 'Authorized Contact (Optional)',
  newCustomerFirstName: 'First Name',
  newCustomerLastName: 'Last Name',
  newCustomerSecondSurname: 'Second Surname',
  newCustomerPhFirstName: 'e.g. Elena',
  newCustomerPhLastName: 'e.g. Martinez',
  newCustomerPhSecondSurname: 'e.g. Lopez',
  newCustomerMobile: 'Mobile',
  newCustomerPreferredContact: 'Preferred Contact Method',
  newCustomerPrefMobile: 'Mobile',
  newCustomerPrefEmail: 'Email',
  newCustomerEmail: 'Email',
  newCustomerPhEmail: 'name@email.com',
  newCustomerPreferredLanguage: 'Preferred Language',
  newCustomerLangEs: 'Spanish',
  newCustomerLangEn: 'English',
  newCustomerLangAr: 'Arabic',
  newCustomerLangFr: 'French',
  newCustomerAuthToggle:
    'This customer has an authorized contact who can act on their behalf',
  newCustomerAuthContactName: 'Contact Name',
  newCustomerAuthRelationship: 'Relationship',
  newCustomerPhFullName: 'Full name',
  newCustomerPhRelationship: 'e.g. Spouse, Parent, Lawyer',
  newCustomerPhAuthPhone: '+34 6XX XXX XXX',
  mobileFieldVerify: 'Verify',
  mobileFieldVerified: 'Verified',
  mobileFieldChannel: 'Channel',
  mobileFieldPhPhone: '6XX XXX XXX',
  mobileFieldPhCode: '6-digit code',
  mobileFieldConfirm: 'Confirm',
  mobileFieldCancel: 'Cancel',
  mobileFieldCodeSentWhatsApp: 'Code sent via WhatsApp',
  mobileFieldCodeSentSms: 'Code sent via SMS',
  preferences: 'Preferences',
  newCustomerOtpPendingHint: 'Verify or cancel the current OTP request before editing this contact detail.',

  profileLoadingCustomer: 'Loading customer profile...',
  profileCustomerNotFound: 'Customer not found',
  profileBackShort: 'Back',
  profileBreadcrumbApplications: 'Applications',
  profileBreadcrumbFamily: 'Family',
  profileCustomerFallback: 'Customer',
  profileUpdatingFamilyGroup: 'Updating family group...',
  profilePrimaryContactNow: 'You are the primary contact now.',
  profileNoContactData: 'No contact data',
  profileFollowUpMarked: 'Follow-up marked',
  profileMarkFollowUp: 'Mark for follow-up',
  profileInquiryYes: 'Inquiry: yes',
  profileInquiryNo: 'Inquiry: no',
  profileTabDetails: 'Details',
  profileTabHistory: 'History',
  profileFamilyGroupTitle: 'Family Group',
  profileFamilyNotLinked: 'This customer is not linked to a family group yet.',
  profileFamilyNameLabel: 'Family name',
  profileFamilyNamePlaceholder: 'Family name',
  profileCreatingFamily: 'Creating...',
  profileCreateFamilyGroup: 'Create Family Group',
  profileRecentHistorySidebar: 'Recent history',
  profileNoAdvisorNotesShort: 'No advisor notes or activities yet.',
  profileHistoryTitle: 'History',
  profileHistorySubtitle: 'Notes and actions for this customer',
  profileHistoryFilterAllEvents: 'All events',
  profileHistoryFilterNotes: 'Notes',
  profileHistoryFilterActivity: 'Activity',
  profileHistoryRangeAllTime: 'All time',
  profileHistoryRangeLast30d: 'Last 30 days',
  profileLoadingHistoryShort: 'Loading history…',
  profileNoEventsMatch: 'No events match these filters.',
  profileAdvisorLogHeading: 'Advisor log',
  profileNoAdvisorLoggedShort: 'No advisor notes or activities logged yet.',
  profileCouldNotLoadHistory: 'Could not load advisor history.',
  profileCouldNotPostHistory: 'Could not post to history',
  profileFamilyDefaultName: 'Family',

  detailsCardTitle: 'Customer Details',
  detailsCardEditing: 'Editing',
  detailsCardEdit: 'Edit',
  detailsCardEditAria: 'Edit customer details',
  detailsCardRegisteredYes: 'Yes',
  detailsCardRegisteredNo: 'No',
  detailsCardCountryOrigin: 'Country of Origin',
  detailsCardCustomerSince: 'Customer Since',
  detailsCardRegistered: 'Registered',
  detailsCardCustomerId: 'Customer ID',
  detailsCardPrimaryAddress: 'Primary address',
  detailsCardPrimaryAddressHint: 'Full address is stored on each case application.',
  detailsCardAuthorizedHeader: 'Authorized contact',
  detailsCardContactMobile: 'Contact mobile',
  detailsCardContactEmailLabel: 'Contact email',
  detailsCardSecondSurnameOptional: 'Second Surname (optional)',
  detailsCardResidentialNote: 'Residential address is captured and edited on each case application.',
  detailsCardAuthOptionalTitle: 'Authorized contact (optional)',
  detailsCardAuthOptionalHint: "Third party allowed to speak on the customer's behalf.",
  detailsCardHasAuthorizedContact: 'Has an authorized contact',
  detailsCardSave: 'Save',
  detailsCardCancel: 'Cancel',
  detailsCardSaveNoData: 'Save did not return updated customer data. Please refresh the page.',
  detailsCardSaveFailed: 'Could not save changes. Please try again.',
  detailsCardEmailPreferred: 'Email preferred',
  detailsCardPreferredShort: 'Preferred',

  activeCaseEyebrow: 'Active case',
  activeCaseNoOpenTitle: 'No open case',
  activeCaseNoActiveTitle: 'No active case',
  activeCaseNoActiveBody: '{{name}} does not have an open application yet.',
  activeCaseOpenNewCase: 'Open New Case',
  activeCaseResidencyBlurb: 'Residency application - track progress and documents below.',
  activeCaseDateOpened: 'Date opened',
  activeCaseStatusLabel: 'Status',
  activeCaseAssignedAdvisor: 'Assigned advisor',
  activeCaseUnassigned: 'Unassigned',
  activeCaseDocuments: 'Documents',
  activeCaseAllUploaded: 'All sections uploaded',
  activeCaseStillNeeded: '{{count}} still needed',
  activeCaseOpenCase: 'Open case',
  activeCaseApplicationFallback: 'Application',

  familyMembersTitle: '{{name}} Members',
  familyAddMember: 'Add Family Member',
  familyFull10: 'Family full (10)',
  familyNoLinkedMembers: 'No linked members found.',
  familyNoContactInfo: 'No contact info',
  familyOpenProfile: 'Profile',
  familyModalTitle: 'Add Family Member',
  familyModalSubtitle: 'Create a user and attach them to this family group.',
  familyLabelFirstNameReq: 'First name *',
  familyLabelLastNameReq: 'Last name *',
  familyLabelRelationshipReq: 'Relationship *',
  familyLabelPreferredLanguage: 'Preferred language',
  familyLabelMobile: 'Mobile',
  familyLabelEmail: 'Email',
  familyPhMobile: '+34 600 000 000',
  familyPhEmail: 'email@example.com',
  familyModalCancel: 'Cancel',
  familySaveBusy: 'Saving...',
  familyFullLabel: 'Family full',
  familyAddToFamily: 'Add to Family',
  familyToastAdded: 'Member added',
  familyToastFailed: 'Failed to add member',
  familyRelSpouse: 'Spouse',
  familyRelChild: 'Child',
  familyRelParent: 'Parent',
  familyRelSibling: 'Sibling',
  familyRelOther: 'Other',

  activityEntryInternalNote: 'Internal Note',
  activityEntryCallInbound: 'Phone Call (Inbound)',
  activityEntryCallOutbound: 'Phone Call (Outbound)',
  activityEntryWhatsAppInbound: 'WhatsApp (Inbound)',
  activityEntryWhatsAppOutbound: 'WhatsApp (Outbound)',
  activityEntryEmailInbound: 'Email (Inbound)',
  activityEntryEmailOutbound: 'Email (Outbound)',
  activityEntrySmsInbound: 'SMS (Inbound)',
  activityEntrySmsOutbound: 'SMS (Outbound)',
  activityTitleCallInbound: 'Inbound Call',
  activityTitleCallOutbound: 'Outbound Call',
  activityTitleWhatsAppInbound: 'WhatsApp Received',
  activityTitleWhatsAppOutbound: 'WhatsApp Sent',
  activityTitleEmailInbound: 'Email Received',
  activityTitleEmailOutbound: 'Email Sent',
  activityTitleSmsInbound: 'SMS Received',
  activityTitleSmsOutbound: 'SMS Sent',
  activityEntryTypeAria: 'Entry type',
  activityDocTagAria: 'Tag a document',
  activityDocTagTitle: 'Tag a document',
  activityClickToWrite: 'Click to write a note...',
  activityWriteNotePlaceholder: 'Write your note here...',
  activityDocTagPlaceholder: 'Optional document tag',
  activityNotePlaceholder: 'Log a note, call, or message…',
  activityPostPosting: 'Posting…',
  activityPost: 'Post',
  add: 'Add',

  topNavAdvisorsTitle: 'Registered advisors',
  topNavAdvisorsSubtitle:
    'Remove another advisor’s account below. You cannot delete yourself or the last admin.',
  topNavCloseAria: 'Close',
  customerLinkModalTitle: 'Customer signup link',
  customerLinkModalSubtitle:
    'Share this link with customers so they can register under the selected project. Each project has its own link.',
  customerLinkProjectLabel: 'Project: {{name}}',
  customerLinkNoProject: 'Select a project first.',
  projectSwitchLabel: 'Active project',
  customerLinkCopy: 'Copy link',
  customerLinkCopied: 'Copied!',
  customerLinkCouldNotLoad: 'Could not load your customer signup link.',
  topNavLoading: 'Loading…',
  topNavCouldNotLoadAdvisors: 'Could not load advisors.',
  topNavNoAdmins: 'No admin accounts found.',
  topNavYou: 'You',
  topNavDeleting: 'Deleting…',
  topNavDelete: 'Delete',
  topNavDeleteAdvisorConfirm:
    'Permanently delete advisor “{{label}}” and all associated data (sessions, profile, documents)? This cannot be undone.',
  topNavDeleteAdvisorFailed: 'Failed to delete advisor.',
  topNavPortalModalTitle: 'Roles & default cases',
  topNavRoleSectionTitle: 'Customer vs advisor',
  topNavRoleSectionDesc:
    'Promote a registered user to advisor (admin), or demote an advisor to customer. Partner accounts cannot be changed here.',
  topNavRoleFilterPlaceholder: 'Filter by name, email, phone, ID…',
  topNavSelectUser: 'Select a user…',
  topNavSelectedLine: 'Selected: ID {{id}}{{email}}{{phone}}',
  topNavPartnerNote: 'This account is a partner; use the database or a dedicated flow to change it.',
  topNavSetAsCustomer: 'Set as customer',
  topNavSetAsAdvisor: 'Set as advisor',
  topNavRoleUpdated: 'Role updated.',
  topNavCouldNotUpdateRole: 'Could not update role.',
  topNavDefaultAdvisorTitle: 'Default advisor for new sign-ups',
  topNavDefaultAdvisorDesc:
    'New application cases created from the customer registration flow are assigned to this advisor automatically (unless an admin creates the case or assigns it elsewhere).',
  topNavDefaultAdvisorNone: 'None (cases stay unassigned)',
  topNavSaveDefault: 'Save default',
  topNavDefaultAdvisorSaved: 'Default advisor saved.',
  topNavCouldNotSaveSettings: 'Could not save settings.',
  topNavCouldNotLoadPortal: 'Could not load portal data.',
  topNavBulkTitle: 'Existing unassigned cases',
  topNavBulkDesc: 'Assign every UNIFIED case that has no advisor yet to the selected advisor.',
  topNavBulkSelectAdvisor: 'Select advisor…',
  topNavBulkAssign: 'Assign all unassigned cases',
  topNavChooseAdvisorFirst: 'Choose an advisor first.',
  topNavBulkConfirm:
    'Assign every unassigned application case (UNIFIED, no advisor) to this advisor? This cannot be undone in one click.',
  topNavBulkUpdatedOne: 'Updated 1 case.',
  topNavBulkUpdatedMany: 'Updated {{count}} cases.',
  topNavBulkAssignFailed: 'Bulk assign failed.',

  historyLogEmpty: 'No history events.',
  historyLogTag: 'Tag:',
  historyLogType_internal_note: 'Internal note',
  historyLogType_call_inbound: 'Phone call (inbound)',
  historyLogType_call_outbound: 'Phone call (outbound)',
  historyLogType_whatsapp_inbound: 'WhatsApp (inbound)',
  historyLogType_whatsapp_outbound: 'WhatsApp (outbound)',
  historyLogType_email_inbound: 'Email (inbound)',
  historyLogType_email_outbound: 'Email (outbound)',
  historyLogType_note: 'Note',
  historyLogType_activity: 'Activity',
  historyLogType_event: 'Event',

  genFormTitle: 'Generate {{code}} Form',
  genFormDraftBadge: 'Draft',
  genFormReadyBadge: 'Ready',
  genFormCloseAria: 'Close',
  genFormDraftMsg:
    'Some case information is incomplete. The form will be generated with a <draft>DRAFT</draft> watermark.',
  genFormFinalMsg:
    'Case data looks complete. The PDF will be generated <no>without</no> a draft watermark.',
  genFormFormDetailsHeading: 'Form details',
  genFormFormLabel: 'Form',
  genFormDraftInline: 'Draft',
  genFormLanguageLabel: 'Language',
  genFormDateLabel: 'Date',
  genFormDataNoteLong:
    'Data will be pulled from Identity, Address, Representative, and Notification Address sections. Missing fields will appear blank on the form.',
  genFormCancel: 'Cancel',
  genFormGeneratePdfDraft: 'Generate PDF (Draft)',
  genFormGeneratePdf: 'Generate PDF',
  genFormSuccessTitle: 'Form generated',
  genFormSuccessTitleDraftSuffix: ' (draft)',
  genFormSuccessFileReady: 'is ready',
  genFormDownloadPdf: 'Download PDF',
  genFormClose: 'Close',

  downloadPkgTitle: 'Download Package',
  downloadPkgPackageIncomplete: 'Package is incomplete -',
  downloadPkgDocsStillOne: '{{count}} document still needed',
  downloadPkgDocsStillMany: '{{count}} documents still needed',
  downloadPkgEx10DraftInPackage:
    'The included EX-10 is generated as a <draft>draft</draft> (application not finalised).',
  downloadPkgAllComplete:
    'All required documents are present and the application is submitted - package is complete.',
  downloadPkgIncludedHeading: 'Included in package',
  downloadPkgManifestHeading: 'Document manifest',
  downloadPkgManifestAccepted: 'Accepted',
  downloadPkgManifestPendingReview: 'Pending review',
  downloadPkgManifestRejected: 'Rejected',
  downloadPkgManifestMissing: 'Missing',
  downloadPkgRowEx10Title: 'EX-10 Application Form',
  downloadPkgRowEx10Subtitle: 'Auto-generated PDF - Spanish (official)',
  downloadPkgBadgeDraft: 'Draft',
  downloadPkgBadgeIncluded: 'Included',
  downloadPkgBadgeAccepted: 'Accepted',
  downloadPkgBadgePending: 'Pending',
  downloadPkgUploadedFile: 'Uploaded file',
  downloadPkgCloseBtn: 'Close',
  downloadPkgBuilding: 'Building ZIP…',
  downloadPkgDownloadZip: 'Download ZIP',
  downloadPkgZipError:
    'Could not build the package. Try again or download files individually.',
  downloadPkgMissingCase: 'Missing case context.',

  serviceFeeTitle: 'Service Fee',
  serviceFeeConfirmChanges: 'Confirm changes',
  serviceFeeLabel: 'Fee',
  serviceFeeCampaign: 'Campaign',
  serviceFeeRemoved: 'Removed',
  serviceFeeReason: 'Reason',
  serviceFeeHistoryRecorded: 'This change will be recorded in history.',
  serviceFeeSaving: 'Saving…',
  serviceFeeConfirm: 'Confirm',
  serviceFeeBack: 'Back',
  serviceFeeAmountLabel: 'Amount ({{currency}})',
  serviceFeeOverrideBadge: 'override',
  serviceFeeReasonOverride: 'Reason for override',
  serviceFeeReasonPlaceholderEdit: 'e.g. Hardship discount approved',
  serviceFeeReset: 'Reset',
  serviceFeeCampaignCodeLabel: 'Campaign code',
  serviceFeeCampaignCodeOptional: '(optional)',
  serviceFeeRemove: 'Remove',
  serviceFeeLookup: 'Look up',
  serviceFeeCodeNotFound: 'Code not found',
  serviceFeeCampaignHintEdit: 'Adding a campaign updates the price',
  serviceFeeCampaignPlaceholder: 'e.g. 9BE96',
  serviceFeeContinue: 'Continue',
  serviceFeeCancel: 'Cancel',
  serviceFeeNoCampaign: 'No campaign attached',
  serviceFeeEditAria: 'Edit service fee',
  serviceFeeToastUpdated: 'Fee updated',
  serviceFeeToastSaveFailed: 'Could not save fee',
  historyNoEntriesMatch: 'No entries match this filter',
  historyDocOtherId: 'Other ID',
  historyDocAsylumDocuments: 'Asylum documentation',
  historyDocCriminalRecordCertificate: 'Criminal record certificate',
  historyDocProofContinuousResidence: 'Proof of residence',
  historyCriminalRecordNote: 'Per country, 3-month validity',
  historyEmployment: 'Employment',
  historyVulnerabilityReport: 'Vulnerability report',
  historyDocumentUploaded: 'Document uploaded - {{document}}',
  historyDocumentAccepted: 'Document accepted - {{document}}',
  historyAdvisorAssigned: 'Advisor assigned',
  historyCaseOpened: 'Case opened',
  historyHelpRequest: 'Applicant asked for help',
  historyHelpReplied: 'Replied to applicant\'s question',
  helpPending: 'Awaiting response',
  helpResponded: 'Responded',
  helpModalTitle: 'Applicant\'s question',
  familyInterestModalTitle: 'Family application interest',
  historyFamilyApplicationInterest: 'Family application interest',
  historyFamilyInterestReplied: 'Replied to family application interest',
  historyFamilyInterestResponded: 'Responded to family application interest',
  helpModalSection: 'Section',
  helpModalClose: 'Close',
  helpReplyLabel: 'Your reply',
  helpReplyPlaceholder: 'Write your response…',
  helpReplyAndResolve: 'Reply & resolve',
  helpMarkRespondedBtn: 'Resolve without reply',
  helpRespondingBtn: 'Saving…',
  helpMarkedResponded: 'Marked as responded - follow-up cleared',
  helpReplySent: 'Reply sent & follow-up cleared',
  historyFileAccepted: '{{file}} was accepted.',
  historyFileLabel: 'File: {{file}}',
  historyCaseAssignedBody: 'Case assigned to {{name}}.',
  historyCaseOpenedBody:
    'Application case created (UNIFIED). The applicant can continue in the chat or complete uploads through the portal.',

  caseHeaderDefaultTitle: 'Case Detail',
  caseActionsSaveDraft: 'Save Draft',
  caseActionsSubmit: 'Submit',
  deleteUserConfirm: 'Delete {{name}}? This action cannot be undone.',
  advisorNameFallback: 'Advisor',
  fieldNie: 'NIE',
  sexMale: 'Male',
  sexFemale: 'Female',
  sexUndetermined: 'Undetermined',
};

export const advisorPortalEs = {
  activeCasesTitle: 'Casos activos',
  activeCasesSubtitle: 'Solicitudes abiertas asignadas a tu equipo.',
  customerSearchTitle: 'Búsqueda de clientes',
  customerSearchSubtitle: 'Busca clientes por nombre, móvil, correo o ID',
  refreshTitle: 'Actualizar lista',
  tabActiveCases: 'Casos activos',
  tabCustomerSearch: 'Búsqueda de clientes',
  addCustomer: 'Añadir cliente',
  showingLabel: 'Mostrando:',
  myApplications: 'Mis solicitudes',
  specificAdvisor: 'Asesor específico',
  allApplications: 'Todas las solicitudes',
  assignedTo: 'Asignado a',
  advisorNumber: 'Asesor n.º {{id}}',
  searchPlaceholderActive: 'Buscar por nombre, móvil, correo, ID de cliente, ID de caso...',
  filterStatusLabel: 'Estado',
  filterSortLabel: 'Ordenar por',
  filterQuickLabel: 'Filtros rápidos',
  compactFiltersBarLabel: 'Filtros de casos activos (barra inferior)',
  compactSearchPh: 'Buscar…',
  allStatuses: 'Todos los estados',
  statusNew: 'Nuevo',
  statusInProgress: 'En curso',
  statusPendingReview: 'Pendiente de revisión',
  statusWaitingOnClient: 'Pendiente del cliente',
  statusWaitingOnDocuments: 'Pendiente de documentos',
  statusReadyToSubmit: 'Listo para enviar',
  statusSubmitted: 'Enviado',
  statusInReviewGovernment: 'En revisión',
  statusApproved: 'Aprobado',
  statusRejected: 'Rechazado',
  statusCancelledClient: 'Cancelado (Cliente)',
  statusCancelledInternal: 'Cancelado (Interno)',
  statusExpiredAbandoned: 'Expirado / Abandonado',
  statusCompleted: 'Completado',
  statusArchived: 'Archivado',
  allCaseTypes: 'Todos los tipos de caso',
  caseTypeDNV: 'Nómada Digital (DNV)',
  caseTypeASO: 'Arraigo Social (ASO)',
  caseTypeALA: 'Arraigo Laboral (ALA)',
  caseTypeAFF: 'Arraigo Familiar (AFF)',
  caseTypeEUF: 'Familiar de la UE (EUF)',
  caseTypeTFR: 'Reagrupación Familiar (TFR)',
  caseTypeINV: 'Golden Visa (INV)',
  caseTypeSTU: 'Estudiante (STU)',
  caseTypeNLV: 'No Lucrativa (NLV)',
  caseTypeHQP: 'Altamente Cualificado (HQP)',
  caseTypeSTA: 'Startup (STA)',
  caseTypeRAE: 'Cuenta Propia (RAE)',
  allAdvisors: 'Todos los asesores',
  unassigned: 'Sin asignar',
  needsAttention: 'Requiere atención',
  sortStatusDateNewest: 'Fecha de estado: más reciente',
  sortStatusDateOldest: 'Fecha de estado: más antigua',
  casesCount: '{{count}} casos',
  casesCountOne: '1 caso',
  customersCount: '{{count}} clientes',
  customersCountOne: '1 cliente',
  clearAll: 'Borrar filtros',
  loadingCases: 'Cargando casos...',
  loadingCustomers: 'Cargando clientes...',
  dirSearchPlaceholder: 'Buscar por nombre, correo, móvil, ID de cliente...',
  dirAll: 'Todos',
  dirFollowUp: 'Seguimiento',
  dirRenewal: 'Renovacion pendiente',
  dirInquiry: 'Consulta',
  deleteUserFailed: 'No se pudo eliminar el usuario. Inténtalo de nuevo.',
  tableCustomer: 'Cliente',
  tableFollowUp: 'Seguimiento',
  tableCaseType: 'Tipo de caso',
  tableContact: 'Móvil / correo',
  tableStatus: 'Estado',
  tableStatusDate: 'Fecha de estado',
  tableAdvisor: 'Asesor',
  tableDocs: 'Docs',
  tableOpen: 'Abrir',
  noApplicationsFound: 'No se encontraron solicitudes',
  familySuffix: 'familia',
  familyLine: 'Familia {{name}}',
  defaultFamilyName: 'Familia',
  defaultCaseType: 'Regularización 2026',
  brandSubtitle: 'Portal del asesor',
  roleAdvisor: 'Asesor',
  menuShowAdvisors: 'Ver todos los asesores',
  menuPortalSettings: 'Roles y casos por defecto',
  menuCustomerSignupLink: 'Enlace de registro para clientes',
  menuLogOut: 'Cerrar sesión',
  menuAriaLabel: 'Menú del asesor, {{name}}',
  workflowNew: 'Nuevo',
  workflowInProgress: 'En curso',
  workflowReadyToApprove: 'Listo para aprobar',
  workflowCompleted: 'Completado',
  workflowArchived: 'Archivado',

  dirNoCustomers: 'No se encontraron clientes',
  dirHeaderCustomer: 'Cliente',
  dirHeaderContact: 'Móvil / correo',
  dirHeaderFollowUp: 'Seguimiento',
  dirHeaderAddedOn: 'Alta',
  dirHeaderLastActivity: 'Última actividad',
  followUpBadge: 'Seguimiento',
  dirOpen: 'Abrir',
  dirDelete: 'Eliminar',
  dirDeleting: 'Eliminando…',
  dirUserNumber: 'Usuario n.º {{id}}',
  dirFamilyLine: 'Familia {{name}}',

  tabCaseDetails: 'Detalles',
  tabHistory: 'Historial',
  tabDocuments: 'Documentos',
  tabDocumentsTooltip:
    'Secciones con al menos una subida. Totalmente aprobadas (todos los archivos aceptados): {{approved}}/{{total}}.',
  loadingCaseDetails: 'Cargando detalles del caso…',
  caseDetailsCardTitle: 'Detalles del caso',
  caseNumber: 'Número de caso',
  caseType: 'Tipo de caso',
  assignedAdvisor: 'Asesor asignado',
  caseStatus: 'Estado',
  generateForm: 'Generar formulario',
  downloadPackage: 'Descargar paquete',
  draftBanner: 'BORRADOR - Esta solicitud no se ha enviado.',
  recentHistory: 'Historial reciente',
  addNoteTitle: 'Agregar nota',
  viewAll: 'Ver todo',
  noHistoryYet: 'Aún no hay historial.',
  documentsCardTitle: 'Documentos',
  manage: 'Gestionar',
  documentsProgress: '{{up}} de {{total}} con subidas · {{ap}} de {{total}} totalmente aprobadas',
  docRowPending: 'Pendiente',
  fileFallback: 'Archivo',
  activityPageTitle: 'Historial',
  activityFilterAll: 'Todo',
  activityFilterNotes: 'Notas',
  activityFilterActivity: 'Actividad',

  docCat_passport_name: 'Pasaporte',
  docCat_passport_desc: 'Pasaporte completo - todas las páginas. Se aceptan caducados.',
  docCat_proof_of_residency_name: 'Justificante de residencia',
  docCat_proof_of_residency_desc: 'Documentos que acrediten estancia continuada en España.',
  docCat_criminal_record_name: 'Antecedentes penales',
  docCat_criminal_record_desc: 'Antecedentes penales - apostillados y traducidos, con antigüedad inferior a 3 meses.',
  docCat_asylum_receipt_name: 'Resguardo de asilo',
  docCat_asylum_receipt_desc: 'Resguardo de asilo - presentado antes del 1 ene 2026.',
  docCat_asylum_receipt_note: 'Debe presentarse antes del 1 de enero de 2026',
  docCat_other_name: 'Otros',
  docCat_other_desc: 'Documentos de apoyo adicionales.',

  sidebarNone: 'Ninguno',
  sidebarAwaiting: 'Pendiente de subida',
  sidebarUploadedN: '{{count}} subidos',
  sidebarComplete: 'Completo',
  sidebarRejected: 'Rechazado',
  sidebarPending: 'Pendiente',

  reviewAccepted: 'Aceptado',
  reviewRejected: 'Rechazado',
  reviewPending: 'Pendiente',
  reviewAwaitingUpload: 'Pendiente de subida',
  reviewAwaiting: 'En espera',
  reviewOptionApproved: 'Aprobado',

  asylumBefore: 'Presentada antes del 1 ene 2026',
  asylumAfter: 'Presentada el 1 ene 2026 o después',
  asylumNever: 'Nunca presentada',

  caseSource: 'Origen',
  caseCreatedAt: 'Fecha de creación',
  caseSourceInPerson: 'Presencial',
  breadcrumbBack: 'Volver',
  breadcrumbApplications: 'Solicitudes',
  loadingCaseShort: 'Cargando caso…',
  invalidCaseId: 'Identificador de caso no válido o desconocido.',
  postedToHistory: 'Publicado en el historial',
  couldNotPostNote: 'No se pudo publicar la nota',

  docBadgeAwaitingUpload: 'Pendiente de subida',
  docBadgePendingReview: 'Pendiente de revisión',
  docBadgeAccepted: 'Aceptado',
  docBadgeRejected: 'Rechazado',
  docUploadCount: '{{count}} archivos',
  docFileExtFallback: 'ARCH',
  toastDownloadFailed: 'No se pudo descargar el archivo',
  toastMissingSession: 'Falta la sesión - recarga la página del caso',
  toastNoFile: 'No hay archivo seleccionado',
  toastUploaded: '{{name}} subido',
  toastUploadFailed: 'Error al subir',
  toastRestoreBeforeStatus: 'Restaura el archivo antes de cambiar el estado de revisión',
  toastStatusUpdated: 'Estado actualizado',
  toastUpdateFailed: 'Error al actualizar',
  toastEnterRejectReason: 'Indica el motivo del rechazo',
  toastDocumentRejected: 'Documento rechazado',
  toastRejectFailed: 'Error al rechazar',
  toastPreviewLoadFailed: 'No se pudo cargar la vista previa.',
  toastFileRestored: 'Archivo restaurado',
  toastRestoreFailed: 'Error al restaurar',
  toastFileRemoved: 'Archivo eliminado',
  toastFileArchived: 'Archivo archivado',
  toastRequestFailed: 'Error en la solicitud',
  previewCloseAria: 'Cerrar vista previa',
  previewLoading: 'Cargando vista previa…',
  previewEmptyFile: '(archivo vacío)',
  previewUnsupportedHint:
    'No hay vista previa en el navegador para este tipo de archivo. Usa Descargar para abrirlo en tu dispositivo.',
  previewClose: 'Cerrar',
  docDeleteOrArchiveAria: 'Archivar o eliminar archivo',
  docDeleteFileAria: 'Eliminar archivo',
  docLabelDocument: 'Documento',
  docSectionUploaded: 'Subidos',
  docLabelNew: 'Nuevo',
  docPlaceholderRejectReason: 'Motivo del rechazo',
  docConfirmRejection: 'Confirmar rechazo',
  docCancel: 'Cancelar',
  docUploadReplacement: 'Sustituir archivo',
  docUploadFile: 'Subir archivo',
  docOr: 'o',
  docScanDocument: 'Escanear documento',
  docTypesHint: 'PDF, JPG, PNG, HEIC → se guardan como PDF',
  docLabelDocumentType: 'Tipo de documento',
  docSelectType: 'Seleccionar tipo',
  docFrom: 'Desde',
  docTo: 'Hasta',
  docShortDescLabel: 'Descripción breve (opcional, máx. 100 caracteres)',
  docShortDescPlaceholder: 'p. ej. contrato laboral anterior',
  docDropBrowse: 'Arrastra un archivo aquí o elige uno',
  docUploading: 'Subiendo…',
  docDeletePermanent: 'Eliminar definitivamente',
  docMoveArchive: 'Mover al archivo',
  docDeleteModalTitlePermanent: '¿Eliminar este archivo definitivamente?',
  docDeleteModalTitleArchive: '¿Mover este archivo al archivo?',
  docDeleteModalBodyPermanent:
    'Se eliminará el archivo y su copia en el almacenamiento. No se puede deshacer.',
  docDeleteModalBodyArchive:
    'Desaparecerá de la lista pero permanecerá en Archivados más abajo, donde podrás restaurarlo o eliminarlo definitivamente.',
  docAddDocument: 'Añadir documento',
  docArchivedTitle: 'Archivados',
  docArchivedFileCount: '{{count}} archivo(s)',
  docArchivedHelp:
    'Restaurar devuelve el archivo a la lista; eliminar definitivamente lo borra del almacenamiento.',
  docRestore: 'Restaurar',
  docFooterSummaryBase: '{{sections}} secciones obligatorias · {{active}} activos',
  docFooterArchivedSuffix: ' · {{count}} archivados',
  docArchivedBadge: 'Archivado',
  docClosePanelAria: 'Cerrar panel del documento',
  docPreviewHint: 'Abre la vista previa para ver PDF e imágenes en el navegador sin descargar.',
  docPreview: 'Vista previa',
  docDownload: 'Descargar',
  docReviewStatus: 'Estado de revisión',
  docDrawerPendingReview: 'Pendiente de revisión',
  docDrawerAccepted: 'Aceptado',
  docDrawerRejected: 'Rechazado',
  docDateFromLabel: 'Fecha desde',
  docDateToLabel: 'Fecha hasta',
  docDescription: 'Descripción',

  sectionIdentity: 'Identidad',
  identityDocumentsTitle: 'Documentos de identidad',
  identityPassportLabel: 'Pasaporte',
  identityOtherIdLabel: 'Otro documento',
  identityStep1: 'Paso 1 - Completa los datos',
  identityStep2: 'Paso 2 - Sube el documento',
  identityDocumentType: 'Tipo de documento',
  identitySelect: 'Seleccionar...',
  identityNationalId: 'Documento nacional de identidad',
  identityTravelDocument: 'Documento de viaje',
  identityCedula: 'Cédula de inscripción',
  identityIdNumber: 'Número de documento',
  identityFullNamePassport: 'Nombre completo (como en el pasaporte)',
  identityDocTypeLabel: 'Documento de identidad',
  sexMale: 'Hombre',
  sexFemale: 'Mujer',
  sexUndetermined: 'Indeterminado',
  oarCaseNumber: 'Número de expediente OAR',
  otherAsylumDocuments: 'Otros documentos de asilo',
  changeFile: 'Cambiar',
  sectionPersonalInformation: 'Información personal',
  sectionContact: 'Contacto',
  sectionAddress: 'Dirección',
  sectionFamily: 'Familia',
  sectionEngagementConsent: 'Compromiso y consentimientos',
  sectionRepresentative: 'Representante',
  sectionNotificationAddress: 'Dirección de notificaciones',
  pathwayAndScreening: 'Ruta y verificación',
  pathwayAppliedAsylumBefore2026: '¿Solicitó asilo antes del 1 ene 2026?',
  pathwayApplyingFor: 'Solicita por',
  pathwayHasPassport: 'Tiene pasaporte',
  pathwayCriminalRecord: 'Antecedentes penales',
  pathwayPriorApplicationsSpain: 'Solicitudes previas en España',
  pathwayWork: 'Situación Irregular (Trabajo)',
  pathwayFamily: 'Situación Irregular (Familia)',
  pathwayVulnerability: 'Vulnerabilidad',
  subsectionIdentity: 'Identidad',
  subsectionOther: 'Otros',
  familyFather: 'Padre',
  familyMother: 'Madre',
  familyParents: 'Padres',
  familySpouse: 'Cónyuge',
  familyNoSpouse: 'Sin cónyuge',
  familyChildrenCount: 'Hijos ({{count}})',
  familyChildrenPresent: 'Hay hijos registrados',
  familyNoChildren: 'Sin hijos',
  familyDependentParentsCount: 'Padres dependientes ({{count}})',
  familyDependentParentsPresent: 'Hay padres dependientes registrados',
  familyNoDependentParents: 'Sin padres dependientes',
  familyHasSpouse: 'Tiene cónyuge',
  familyHasChildren: 'Tiene hijos',
  familyHasDependentParents: 'Tiene padres a cargo',
  engagementConsent: 'Consentimiento de encargo',
  dataProcessingConsent: 'Consentimiento de tratamiento de datos',
  noReturnCommitment: 'Compromiso de no retorno',
  noHealthcareCoverage: 'Sin cobertura sanitaria',
  signature: 'Firma',
  applicantRequestedQuestions: 'El solicitante pidió aclaraciones antes de firmar',
  unknownValue: 'Desconocido',
  yes: 'Sí',
  no: 'No',
  approve: 'Aprobar',
  caseApproved: 'Caso aprobado',
  caseApproveFailed: 'No se pudo aprobar el caso',
  caseTypeUpdated: 'Tipo de caso actualizado',
  caseTypeUpdateFailed: 'No se pudo actualizar el tipo de caso',
  editCaseTypeAria: 'Editar tipo de caso',
  sectionEdit: 'Editar',
  sectionSave: 'Guardar',
  sectionSaving: 'Guardando...',
  sectionCancel: 'Cancelar',
  referralCode: 'Código de referido',
  referralPlaceholder: 'p. ej. R74321',
  referralLookup: 'Buscar',
  referralSelfError: 'El solicitante no puede ser su propio referente.',
  referralCodeNotFound: 'Código no encontrado',
  referralNone: 'Ninguno',
  editReferralAria: 'Editar referido',
  addReferralAria: 'Añadir referido',
  editPriorAsylumAria: 'Editar asilo previo',
  priorAsylum: 'Asilo previo',
  lastUpdated: 'Última actualización',
  historyLoading: 'Cargando historial…',
  historyEmpty: 'Aún no hay eventos en el historial.',
  qualificationTagNote: 'Nota',
  qualificationFlow: 'Flujo',
  qualificationOutcome: 'Resultado',
  qualificationQuestionFallback: 'Pregunta',
  qualificationAnswer: 'Respuesta',
  qualificationAnswerYes: 'Sí',
  qualificationAnswerNo: 'No',
  qualificationTitle: 'Respuestas de registro del solicitante',
  historyBy: 'por {{name}}',
  savePostalCode5: 'El código postal debe tener 5 dígitos',
  saveRepresentativePostalCode5: 'El código postal del representante debe tener 5 dígitos',
  saveNotificationPostalCode5: 'El código postal de notificaciones debe tener 5 dígitos',
  validationFirstName: 'El nombre debe tener entre 1 y 40 letras (sin dígitos)',
  validationLastName: 'El apellido debe tener entre 1 y 40 letras (sin dígitos)',
  validationSecondSurname: 'El segundo apellido debe tener hasta 40 letras',
  validationEmail: 'Introduce un correo válido (6-100 caracteres)',
  validationPhone: 'El teléfono debe contener entre 6 y 15 dígitos',
  validationDobAdult: 'La fecha de nacimiento debe ser válida y mayor de 18 años',
  validationPlaceOfBirth: 'El lugar de nacimiento debe tener entre 1 y 40 letras',
  validationFatherName: 'El nombre del padre debe tener hasta 80 letras',
  validationMotherName: 'El nombre de la madre debe tener hasta 80 letras',
  validationPassport: 'El pasaporte debe tener 5-20 letras o dígitos',
  validationNie: 'El NIE debe tener formato X/Y/Z + 7 dígitos + letra',
  validationStreet: 'La calle debe tener 1-100 caracteres válidos',
  validationStreetNumber: 'El número debe tener 1-10 letras o dígitos',
  validationCity: 'La ciudad debe tener entre 1 y 40 letras',
  validationPostalCode: 'El código postal debe tener exactamente 5 dígitos',
  validationRepresentativePhone: 'El teléfono del representante debe tener 6-15 dígitos',
  validationRepresentativeEmail: 'El correo del representante debe ser válido',
  validationNotificationPhone: 'El teléfono de notificaciones debe tener 6-15 dígitos',
  validationNotificationEmail: 'El correo de notificaciones debe ser válido',
  validationAuthorizedName: 'El nombre del contacto autorizado debe tener hasta 40 letras',
  validationAuthorizedPhone: 'El teléfono del contacto autorizado debe tener 6-15 dígitos',
  validationAuthorizedEmail: 'El correo del contacto autorizado debe ser válido',
  dobInputHint: '',
  nieInputPlaceholder: 'p. ej. X1234567L',
  saveSectionFailed: 'No se pudo guardar la sección',
  saveSectionDone: 'Sección guardada',
  savePartial: 'Guardados {{count}}. Error: {{reason}}',
  referralSaved: 'Referido guardado',
  referralRemoved: 'Referido eliminado',
  referralSaveFailed: 'No se pudo guardar el referido',
  referralRemoveFailed: 'No se pudo eliminar el referido',
  genericSaved: 'Guardado',
  genericCouldNotSave: 'No se pudo guardar',
  needsAttentionMarked: 'Caso marcado como requiere atención',
  needsAttentionCleared: 'Se quitó de requiere atención',
  needsAttentionUpdateFailed: 'No se pudo actualizar la marca',
  caseLabelFallback: 'Caso',
  needsAttentionTitleClear: 'Quitar marca de requiere atención',
  needsAttentionTitleMark: 'Marcar este caso para seguimiento',
  needsAttentionSaving: 'Guardando…',
  needsAttentionLabel: 'Requiere atención',
  needsAttentionMarkLabel: 'Marcar requiere atención',
  downloadStarted: 'Descarga iniciada - guardando {{name}}. Revisa tu carpeta de Descargas.',
  packageNameFallback: 'paquete',
  familyApplications: 'Solicitudes',
  familySuffixTitle: 'Familia',
  familyOverview: 'Resumen del grupo familiar e historial de casos',
  familyBackToSearch: 'Volver a la búsqueda',
  familyCaseHistory: 'Historial de casos',
  familyNoLinkedMembers: 'No hay miembros vinculados.',
  familyMemberName: 'Nombre',
  familyMemberPhone: 'Teléfono',
  familyMemberEmail: 'Correo',
  familyMemberOpen: 'Abrir',
  familyMembersCount: 'Miembros',
  familyWithEmail: 'Con correo',
  familyWithMobile: 'Con móvil',
  familyCaseMember: 'Miembro',
  familyCaseCase: 'Caso',
  familyCaseStatus: 'Estado',
  familyCaseUpdated: 'Actualizado',
  familyNoCaseHistory: 'No se encontró historial de casos.',
  remove: 'Eliminar',
  fieldFirstName: 'Nombre',
  fieldLastName: 'Apellidos',
  fieldSecondSurname: 'Segundo apellido',
  fieldDateOfBirth: 'Fecha de nacimiento',
  fieldArrivalInSpain: 'Llegada a España',
  fieldPlaceOfBirth: 'Lugar de nacimiento',
  fieldCountryOfBirth: 'País de nacimiento',
  fieldNationality: 'Nacionalidad',
  fieldCivilStatus: 'Estado civil',
  fieldFatherName: 'Nombre del padre',
  fieldMotherName: 'Nombre de la madre',
  fieldPassportNumber: 'Número de pasaporte',
  fieldSex: 'Sexo',
  fieldMobile: 'Móvil',
  fieldEmail: 'Correo',
  fieldChannel: 'Canal',
  fieldPreferredContact: 'Contacto preferido',
  fieldPreferredLanguage: 'Idioma preferido',
  fieldStreet: 'Calle',
  fieldStreetAddress: 'Dirección',
  fieldNumber: 'Número',
  fieldFloor: 'Piso',
  fieldDoor: 'Puerta',
  fieldLocality: 'Localidad',
  fieldCity: 'Ciudad',
  fieldProvince: 'Provincia',
  fieldPostalCode: 'Código Postal',
  fieldCountry: 'País',
  fieldResidentSince: 'Residente desde',
  fieldCompany: 'Empresa',
  fieldFirmName: 'Nombre del despacho',
  fieldRepresentativeId: 'ID / documento del representante',
  fieldDniNieCif: 'DNI/NIE/CIF',
  fieldAddress: 'Dirección',
  fieldPhone: 'Teléfono',
  fieldName: 'Nombre',
  fieldDocumentId: 'Documento de identidad',
  fieldElectronicNotifications: 'Notificaciones electrónicas',

  newCustomerCrumbSearch: 'Búsqueda de clientes',
  newCustomerTitle: 'Añadir cliente',
  newCustomerSubtitle: 'Crea un registro de cliente nuevo.',
  newCustomerBackSearch: 'Volver a la búsqueda',
  newCustomerSave: 'Guardar cliente',
  newCustomerSaving: 'Guardando…',
  newCustomerSaved: '¡Guardado!',
  newCustomerCancel: 'Cancelar',
  newCustomerCreateFailed: 'No se pudo crear el cliente',
  newCustomerSecPersonal: 'Datos personales',
  newCustomerSecContact: 'Datos de contacto',
  newCustomerSecAuthorized: 'Contacto autorizado (opcional)',
  newCustomerFirstName: 'Nombre',
  newCustomerLastName: 'Primer apellido',
  newCustomerSecondSurname: 'Segundo apellido',
  newCustomerPhFirstName: 'p. ej. Elena',
  newCustomerPhLastName: 'p. ej. Martínez',
  newCustomerPhSecondSurname: 'p. ej. López',
  newCustomerMobile: 'Móvil',
  newCustomerPreferredContact: 'Forma de contacto preferida',
  newCustomerPrefMobile: 'Móvil',
  newCustomerPrefEmail: 'Correo electrónico',
  newCustomerEmail: 'Correo electrónico',
  newCustomerPhEmail: 'nombre@correo.com',
  newCustomerPreferredLanguage: 'Idioma preferido',
  newCustomerLangEs: 'Español',
  newCustomerLangEn: 'Inglés',
  newCustomerLangAr: 'Árabe',
  newCustomerLangFr: 'Francés',
  newCustomerAuthToggle:
    'Este cliente tiene un contacto autorizado que puede actuar en su nombre',
  newCustomerAuthContactName: 'Nombre del contacto',
  newCustomerAuthRelationship: 'Parentesco o relación',
  newCustomerPhFullName: 'Nombre completo',
  newCustomerPhRelationship: 'p. ej. Cónyuge, padre/madre, abogado',
  newCustomerPhAuthPhone: '+34 6XX XXX XXX',
  mobileFieldVerify: 'Verificar',
  mobileFieldVerified: 'Verificado',
  mobileFieldChannel: 'Canal',
  mobileFieldPhPhone: '6XX XXX XXX',
  mobileFieldPhCode: 'Código de 6 dígitos',
  mobileFieldConfirm: 'Confirmar',
  mobileFieldCancel: 'Cancelar',
  mobileFieldCodeSentWhatsApp: 'Código enviado por WhatsApp',
  mobileFieldCodeSentSms: 'Código enviado por SMS',
  preferences: 'Preferencias',
  newCustomerOtpPendingHint: 'Verifica o cancela la solicitud de OTP actual antes de editar este dato de contacto.',

  profileLoadingCustomer: 'Cargando perfil del cliente…',
  profileCustomerNotFound: 'Cliente no encontrado',
  profileBackShort: 'Volver',
  profileBreadcrumbApplications: 'Solicitudes',
  profileBreadcrumbFamily: 'Familia',
  profileCustomerFallback: 'Cliente',
  profileUpdatingFamilyGroup: 'Actualizando grupo familiar…',
  profilePrimaryContactNow: 'Ahora eres el contacto principal.',
  profileNoContactData: 'Sin datos de contacto',
  profileFollowUpMarked: 'Seguimiento marcado',
  profileMarkFollowUp: 'Marcar para seguimiento',
  profileInquiryYes: 'Consulta: sí',
  profileInquiryNo: 'Consulta: no',
  profileTabDetails: 'Detalles',
  profileTabHistory: 'Historial',
  profileFamilyGroupTitle: 'Grupo familiar',
  profileFamilyNotLinked: 'Este cliente aún no está vinculado a un grupo familiar.',
  profileFamilyNameLabel: 'Nombre de la familia',
  profileFamilyNamePlaceholder: 'Nombre de la familia',
  profileCreatingFamily: 'Creando…',
  profileCreateFamilyGroup: 'Crear grupo familiar',
  profileRecentHistorySidebar: 'Historial reciente',
  profileNoAdvisorNotesShort: 'Aún no hay notas ni actividades del asesor.',
  profileHistoryTitle: 'Historial',
  profileHistorySubtitle: 'Notas y acciones de este cliente',
  profileHistoryFilterAllEvents: 'Todos los eventos',
  profileHistoryFilterNotes: 'Notas',
  profileHistoryFilterActivity: 'Actividad',
  profileHistoryRangeAllTime: 'Todo el período',
  profileHistoryRangeLast30d: 'Últimos 30 días',
  profileLoadingHistoryShort: 'Cargando historial…',
  profileNoEventsMatch: 'Ningún evento coincide con estos filtros.',
  profileAdvisorLogHeading: 'Registro del asesor',
  profileNoAdvisorLoggedShort: 'Aún no hay notas ni actividades registradas.',
  profileCouldNotLoadHistory: 'No se pudo cargar el historial del asesor.',
  profileCouldNotPostHistory: 'No se pudo publicar en el historial',
  profileFamilyDefaultName: 'Familia',

  detailsCardTitle: 'Detalles del cliente',
  detailsCardEditing: 'Editando',
  detailsCardEdit: 'Editar',
  detailsCardEditAria: 'Editar detalles del cliente',
  detailsCardRegisteredYes: 'Sí',
  detailsCardRegisteredNo: 'No',
  detailsCardCountryOrigin: 'País de origen',
  detailsCardCustomerSince: 'Cliente desde',
  detailsCardRegistered: 'Registrado',
  detailsCardCustomerId: 'ID de cliente',
  detailsCardPrimaryAddress: 'Dirección principal',
  detailsCardPrimaryAddressHint: 'La dirección completa figura en cada solicitud de caso.',
  detailsCardAuthorizedHeader: 'Contacto autorizado',
  detailsCardContactMobile: 'Móvil del contacto',
  detailsCardContactEmailLabel: 'Correo del contacto',
  detailsCardSecondSurnameOptional: 'Segundo apellido (opcional)',
  detailsCardResidentialNote:
    'La dirección residencial se recoge y se edita en cada solicitud de caso.',
  detailsCardAuthOptionalTitle: 'Contacto autorizado (opcional)',
  detailsCardAuthOptionalHint: 'Tercero autorizado para hablar en nombre del cliente.',
  detailsCardHasAuthorizedContact: 'Tiene un contacto autorizado',
  detailsCardSave: 'Guardar',
  detailsCardCancel: 'Cancelar',
  detailsCardSaveNoData:
    'El guardado no devolvió los datos del cliente actualizados. Actualiza la página.',
  detailsCardSaveFailed: 'No se pudieron guardar los cambios. Inténtalo de nuevo.',
  detailsCardEmailPreferred: 'Correo preferido',
  detailsCardPreferredShort: 'Preferido',

  activeCaseEyebrow: 'Caso activo',
  activeCaseNoOpenTitle: 'Sin caso abierto',
  activeCaseNoActiveTitle: 'Sin caso activo',
  activeCaseNoActiveBody: '{{name}} aún no tiene una solicitud abierta.',
  activeCaseOpenNewCase: 'Abrir solicitud nueva',
  activeCaseResidencyBlurb: 'Solicitud de residencia: consulta el progreso y los documentos abajo.',
  activeCaseDateOpened: 'Fecha de apertura',
  activeCaseStatusLabel: 'Estado',
  activeCaseAssignedAdvisor: 'Asesor asignado',
  activeCaseUnassigned: 'Sin asignar',
  activeCaseDocuments: 'Documentos',
  activeCaseAllUploaded: 'Todas las secciones ya tienen documentos subidos',
  activeCaseStillNeeded: 'Faltan {{count}}',
  activeCaseOpenCase: 'Abrir caso',
  activeCaseApplicationFallback: 'Solicitud',

  familyMembersTitle: 'Miembros de {{name}}',
  familyAddMember: 'Añadir miembro',
  familyFull10: 'Familia completa (10)',
  familyNoLinkedMembers: 'No hay miembros vinculados.',
  familyNoContactInfo: 'Sin datos de contacto',
  familyOpenProfile: 'Perfil',
  familyModalTitle: 'Añadir miembro a la familia',
  familyModalSubtitle: 'Crea un usuario y vincúlalo a este grupo familiar.',
  familyLabelFirstNameReq: 'Nombre *',
  familyLabelLastNameReq: 'Primer apellido *',
  familyLabelRelationshipReq: 'Parentesco *',
  familyLabelPreferredLanguage: 'Idioma preferido',
  familyLabelMobile: 'Móvil',
  familyLabelEmail: 'Correo',
  familyPhMobile: '+34 600 000 000',
  familyPhEmail: 'correo@ejemplo.com',
  familyModalCancel: 'Cancelar',
  familySaveBusy: 'Guardando…',
  familyFullLabel: 'Familia completa',
  familyAddToFamily: 'Añadir a la familia',
  familyToastAdded: 'Miembro añadido',
  familyToastFailed: 'No se pudo añadir el miembro',
  familyRelSpouse: 'Cónyuge',
  familyRelChild: 'Hijo/a',
  familyRelParent: 'Padre/Madre',
  familyRelSibling: 'Hermano/a',
  familyRelOther: 'Otro',

  activityEntryInternalNote: 'Nota interna',
  activityEntryCallInbound: 'Llamada (entrante)',
  activityEntryCallOutbound: 'Llamada (saliente)',
  activityEntryWhatsAppInbound: 'WhatsApp (entrante)',
  activityEntryWhatsAppOutbound: 'WhatsApp (saliente)',
  activityEntryEmailInbound: 'Correo (entrante)',
  activityEntryEmailOutbound: 'Correo (saliente)',
  activityEntrySmsInbound: 'SMS (entrante)',
  activityEntrySmsOutbound: 'SMS (saliente)',
  activityTitleCallInbound: 'Llamada entrante',
  activityTitleCallOutbound: 'Llamada saliente',
  activityTitleWhatsAppInbound: 'WhatsApp recibido',
  activityTitleWhatsAppOutbound: 'WhatsApp enviado',
  activityTitleEmailInbound: 'Correo recibido',
  activityTitleEmailOutbound: 'Correo enviado',
  activityTitleSmsInbound: 'SMS recibido',
  activityTitleSmsOutbound: 'SMS enviado',
  activityEntryTypeAria: 'Tipo de entrada',
  activityDocTagAria: 'Etiquetar un documento',
  activityDocTagTitle: 'Etiquetar un documento',
  activityClickToWrite: 'Haz clic para escribir una nota...',
  activityWriteNotePlaceholder: 'Escribe tu nota aquí...',
  activityDocTagPlaceholder: 'Etiqueta de documento (opcional)',
  activityNotePlaceholder: 'Añade una nota, llamada o mensaje…',
  activityPostPosting: 'Publicando…',
  activityPost: 'Publicar',
  add: 'Añadir',

  topNavAdvisorsTitle: 'Asesores registrados',
  topNavAdvisorsSubtitle:
    'Elimina la cuenta de otro asesor abajo. No puedes eliminarte a ti mismo ni al último administrador.',
  topNavCloseAria: 'Cerrar',
  customerLinkModalTitle: 'Enlace de registro para clientes',
  customerLinkModalSubtitle:
    'Comparte este enlace con tus clientes para que se registren en el proyecto seleccionado. Cada proyecto tiene su propio enlace.',
  customerLinkProjectLabel: 'Proyecto: {{name}}',
  customerLinkNoProject: 'Selecciona un proyecto primero.',
  projectSwitchLabel: 'Proyecto activo',
  customerLinkCopy: 'Copiar enlace',
  customerLinkCopied: '¡Copiado!',
  customerLinkCouldNotLoad: 'No se pudo cargar el enlace de registro.',
  topNavLoading: 'Cargando…',
  topNavCouldNotLoadAdvisors: 'No se pudo cargar la lista de asesores.',
  topNavNoAdmins: 'No se encontraron cuentas de administrador.',
  topNavYou: 'Tú',
  topNavDeleting: 'Eliminando…',
  topNavDelete: 'Eliminar',
  topNavDeleteAdvisorConfirm:
    '¿Eliminar de forma permanente al asesor «{{label}}» y todos los datos asociados (sesiones, perfil, documentos)? No se puede deshacer.',
  topNavDeleteAdvisorFailed: 'No se pudo eliminar al asesor.',
  topNavPortalModalTitle: 'Roles y casos por defecto',
  topNavRoleSectionTitle: 'Cliente frente a asesor',
  topNavRoleSectionDesc:
    'Asciende a un usuario registrado a asesor (admin) o revierte un asesor a cliente. Las cuentas partner no se pueden cambiar aquí.',
  topNavRoleFilterPlaceholder: 'Filtrar por nombre, correo, teléfono, ID…',
  topNavSelectUser: 'Selecciona un usuario…',
  topNavSelectedLine: 'Seleccionado: ID {{id}}{{email}}{{phone}}',
  topNavPartnerNote:
    'Esta cuenta es partner; usa la base de datos o un flujo dedicado para cambiarla.',
  topNavSetAsCustomer: 'Marcar como cliente',
  topNavSetAsAdvisor: 'Marcar como asesor',
  topNavRoleUpdated: 'Rol actualizado.',
  topNavCouldNotUpdateRole: 'No se pudo actualizar el rol.',
  topNavDefaultAdvisorTitle: 'Asesor por defecto para nuevos registros',
  topNavDefaultAdvisorDesc:
    'Los nuevos casos de solicitud creados desde el registro de clientes se asignan automáticamente a este asesor (salvo que un admin cree el caso o lo asigne en otro sitio).',
  topNavDefaultAdvisorNone: 'Ninguno (los casos quedan sin asignar)',
  topNavSaveDefault: 'Guardar por defecto',
  topNavDefaultAdvisorSaved: 'Asesor por defecto guardado.',
  topNavCouldNotSaveSettings: 'No se pudieron guardar los ajustes.',
  topNavCouldNotLoadPortal: 'No se pudieron cargar los datos del portal.',
  topNavBulkTitle: 'Casos sin asignar existentes',
  topNavBulkDesc: 'Asigna todos los casos UNIFICADOS que aún no tengan asesor al asesor seleccionado.',
  topNavBulkSelectAdvisor: 'Selecciona un asesor…',
  topNavBulkAssign: 'Asignar todos los casos sin asignar',
  topNavChooseAdvisorFirst: 'Elige primero un asesor.',
  topNavBulkConfirm:
    '¿Asignar todos los casos de solicitud sin asignar (UNIFICADOS, sin asesor) a este asesor? No se puede deshacer de un solo clic.',
  topNavBulkUpdatedOne: 'Se actualizó 1 caso.',
  topNavBulkUpdatedMany: 'Se actualizaron {{count}} casos.',
  topNavBulkAssignFailed: 'Error en la asignación masiva.',

  historyLogEmpty: 'No hay eventos en el historial.',
  historyLogTag: 'Etiqueta:',
  historyLogType_internal_note: 'Nota interna',
  historyLogType_call_inbound: 'Llamada (entrante)',
  historyLogType_call_outbound: 'Llamada (saliente)',
  historyLogType_whatsapp_inbound: 'WhatsApp (entrante)',
  historyLogType_whatsapp_outbound: 'WhatsApp (saliente)',
  historyLogType_email_inbound: 'Correo (entrante)',
  historyLogType_email_outbound: 'Correo (saliente)',
  historyLogType_note: 'Nota',
  historyLogType_activity: 'Actividad',
  historyLogType_event: 'Evento',

  genFormTitle: 'Generar formulario {{code}}',
  genFormDraftBadge: 'Borrador',
  genFormReadyBadge: 'Listo',
  genFormCloseAria: 'Cerrar',
  genFormDraftMsg:
    'Falta información del caso. El formulario se generará con una marca de agua de <draft>BORRADOR</draft>.',
  genFormFinalMsg:
    'Los datos del caso parecen completos. El PDF se generará <no>sin</no> marca de agua de borrador.',
  genFormFormDetailsHeading: 'Detalles del formulario',
  genFormFormLabel: 'Formulario',
  genFormDraftInline: 'Borrador',
  genFormLanguageLabel: 'Idioma',
  genFormDateLabel: 'Fecha',
  genFormDataNoteLong:
    'Los datos provienen de las secciones Identidad, Domicilio, Representante y Dirección de notificación. Los campos vacíos aparecerán en blanco en el formulario.',
  genFormCancel: 'Cancelar',
  genFormGeneratePdfDraft: 'Generar PDF (borrador)',
  genFormGeneratePdf: 'Generar PDF',
  genFormSuccessTitle: 'Formulario generado',
  genFormSuccessTitleDraftSuffix: ' (borrador)',
  genFormSuccessFileReady: 'está listo',
  genFormDownloadPdf: 'Descargar PDF',
  genFormClose: 'Cerrar',

  downloadPkgTitle: 'Descargar paquete',
  downloadPkgPackageIncomplete: 'El paquete está incompleto -',
  downloadPkgDocsStillOne: 'falta {{count}} documento',
  downloadPkgDocsStillMany: 'faltan {{count}} documentos',
  downloadPkgEx10DraftInPackage:
    'El EX-10 incluido se genera como <draft>borrador</draft> (solicitud no finalizada).',
  downloadPkgAllComplete:
    'Todos los documentos obligatorios están presentes y la solicitud está enviada: el paquete está completo.',
  downloadPkgIncludedHeading: 'Incluido en el paquete',
  downloadPkgManifestHeading: 'Manifiesto de documentos',
  downloadPkgManifestAccepted: 'Aceptado',
  downloadPkgManifestPendingReview: 'Pendiente de revisión',
  downloadPkgManifestRejected: 'Rechazado',
  downloadPkgManifestMissing: 'Falta',
  downloadPkgRowEx10Title: 'Formulario de solicitud EX-10',
  downloadPkgRowEx10Subtitle: 'PDF autogenerado - español (oficial)',
  downloadPkgBadgeDraft: 'Borrador',
  downloadPkgBadgeIncluded: 'Incluido',
  downloadPkgBadgeAccepted: 'Aceptado',
  downloadPkgBadgePending: 'Pendiente',
  downloadPkgUploadedFile: 'Archivo subido',
  downloadPkgCloseBtn: 'Cerrar',
  downloadPkgBuilding: 'Generando ZIP…',
  downloadPkgDownloadZip: 'Descargar ZIP',
  downloadPkgZipError:
    'No se pudo generar el paquete. Inténtalo de nuevo o descarga los archivos por separado.',
  downloadPkgMissingCase: 'Falta el contexto del caso.',

  serviceFeeTitle: 'Tarifa de servicio',
  serviceFeeConfirmChanges: 'Confirmar cambios',
  serviceFeeLabel: 'Tarifa',
  serviceFeeCampaign: 'Campaña',
  serviceFeeRemoved: 'Eliminada',
  serviceFeeReason: 'Motivo',
  serviceFeeHistoryRecorded: 'Este cambio quedará registrado en el historial.',
  serviceFeeSaving: 'Guardando…',
  serviceFeeConfirm: 'Confirmar',
  serviceFeeBack: 'Volver',
  serviceFeeAmountLabel: 'Importe ({{currency}})',
  serviceFeeOverrideBadge: 'ajuste',
  serviceFeeReasonOverride: 'Motivo del ajuste',
  serviceFeeReasonPlaceholderEdit: 'p. ej. descuento por dificultad aprobado',
  serviceFeeReset: 'Restablecer',
  serviceFeeCampaignCodeLabel: 'Código de campaña',
  serviceFeeCampaignCodeOptional: '(opcional)',
  serviceFeeRemove: 'Quitar',
  serviceFeeLookup: 'Buscar',
  serviceFeeCodeNotFound: 'Código no encontrado',
  serviceFeeCampaignHintEdit: 'Añadir una campaña actualiza el precio',
  serviceFeeCampaignPlaceholder: 'p. ej. 9BE96',
  serviceFeeContinue: 'Continuar',
  serviceFeeCancel: 'Cancelar',
  serviceFeeNoCampaign: 'Sin campaña vinculada',
  serviceFeeEditAria: 'Editar tarifa de servicio',
  serviceFeeToastUpdated: 'Tarifa actualizada',
  serviceFeeToastSaveFailed: 'No se pudo guardar la tarifa',
  historyNoEntriesMatch: 'No hay entradas que coincidan con este filtro',
  historyDocOtherId: 'Otro documento de identidad',
  historyDocAsylumDocuments: 'Documentación de asilo',
  historyDocCriminalRecordCertificate: 'Certificado de antecedentes penales',
  historyDocProofContinuousResidence: 'Prueba de residencia',
  historyCriminalRecordNote: 'Por país, validez de 3 meses',
  historyEmployment: 'Empleo',
  historyVulnerabilityReport: 'Informe de vulnerabilidad',
  historyDocumentUploaded: 'Documento subido - {{document}}',
  historyDocumentAccepted: 'Documento aceptado - {{document}}',
  historyAdvisorAssigned: 'Asesor asignado',
  historyCaseOpened: 'Caso abierto',
  historyHelpRequest: 'El solicitante pidió ayuda',
  historyHelpReplied: 'Respondió a la pregunta del solicitante',
  helpPending: 'Pendiente de respuesta',
  helpResponded: 'Respondido',
  helpModalTitle: 'Pregunta del solicitante',
  familyInterestModalTitle: 'Interés en solicitud familiar',
  historyFamilyApplicationInterest: 'Interés en solicitud familiar',
  historyFamilyInterestReplied: 'Respuesta sobre interés en solicitud familiar',
  historyFamilyInterestResponded: 'Interés en solicitud familiar marcado como respondido',
  helpModalSection: 'Sección',
  helpModalClose: 'Cerrar',
  helpReplyLabel: 'Tu respuesta',
  helpReplyPlaceholder: 'Escribe tu respuesta…',
  helpReplyAndResolve: 'Responder y resolver',
  helpMarkRespondedBtn: 'Resolver sin respuesta',
  helpRespondingBtn: 'Guardando…',
  helpMarkedResponded: 'Marcado como respondido - seguimiento eliminado',
  helpReplySent: 'Respuesta enviada - seguimiento eliminado',
  historyFileAccepted: '{{file}} fue aceptado.',
  historyFileLabel: 'Archivo: {{file}}',
  historyCaseAssignedBody: 'Caso asignado a {{name}}.',
  historyCaseOpenedBody:
    'Caso de solicitud creado (UNIFIED). El solicitante puede continuar en el chat o completar las subidas a través del portal.',

  caseHeaderDefaultTitle: 'Detalle del caso',
  caseActionsSaveDraft: 'Guardar borrador',
  caseActionsSubmit: 'Enviar',
  deleteUserConfirm: '¿Eliminar a {{name}}? Esta acción no se puede deshacer.',
  advisorNameFallback: 'Asesor',
  fieldNie: 'NIE',
  sexMale: 'Hombre',
  sexFemale: 'Mujer',
  sexUndetermined: 'Indeterminado',
};

export const advisorPortalFr = {
  ...advisorPortalEn,
  breadcrumbBack: 'Retour',
  breadcrumbApplications: 'Dossiers',
  generateForm: 'Générer le formulaire',
  downloadPackage: 'Télécharger le dossier',
  draftBanner: "BROUILLON - Cette demande n'a pas encore été soumise.",
  tabCaseDetails: 'Détails',
  tabHistory: 'Historique',
  tabDocuments: 'Documents',
  addNoteTitle: 'Ajouter une note',
  recentHistory: 'Historique récent',
  viewAll: 'Voir tout',
  noHistoryYet: "Aucun historique pour l'instant.",
  activityFilterAll: 'Tous',
  activityFilterNotes: 'Notes',
  activityFilterActivity: 'Activité',
  historyNoEntriesMatch: "Aucune entrée ne correspond à ce filtre",
  sectionIdentity: 'Identité',
  sectionFamily: 'Famille',
  docCat_proof_of_residency_name: 'Preuve de résidence',
  docCat_criminal_record_name: 'Casier judiciaire',
  docCat_other_name: 'Autre',
  reviewAccepted: 'Accepté',
  reviewRejected: 'Rejeté',
  reviewPending: 'En attente',
  sidebarUploadedN: '{{count}} téléversé(s)',
  activityEntryInternalNote: 'Note interne',
  activityEntryCallInbound: 'Appel téléphonique (entrant)',
  activityEntryCallOutbound: 'Appel téléphonique (sortant)',
  activityEntryWhatsAppInbound: 'WhatsApp (entrant)',
  activityEntryWhatsAppOutbound: 'WhatsApp (sortant)',
  activityEntryEmailInbound: 'E-mail (entrant)',
  activityEntryEmailOutbound: 'E-mail (sortant)',
  activityEntrySmsInbound: 'SMS (entrant)',
  activityEntrySmsOutbound: 'SMS (sortant)',
  activityTitleCallInbound: 'Appel entrant',
  activityTitleCallOutbound: 'Appel sortant',
  activityTitleWhatsAppInbound: 'WhatsApp reçu',
  activityTitleWhatsAppOutbound: 'WhatsApp envoyé',
  activityTitleEmailInbound: 'E-mail reçu',
  activityTitleEmailOutbound: 'E-mail envoyé',
  activityTitleSmsInbound: 'SMS reçu',
  activityTitleSmsOutbound: 'SMS envoyé',
  activityEntryTypeAria: "Type d'entrée",
  activityDocTagAria: 'Associer un document',
  activityDocTagTitle: 'Associer un document',
  activityClickToWrite: 'Cliquez pour écrire une note...',
  activityWriteNotePlaceholder: 'Écrivez votre note ici...',
  activityNotePlaceholder: 'Consigner une note, un appel ou un message…',
  activityPostPosting: 'Publication…',
  activityPost: 'Publier',
  add: 'Ajouter',
  mobileFieldVerify: 'Vérifier',
  mobileFieldVerified: 'Vérifié',
  remove: 'Supprimer',
  qualificationTitle: 'Réponses d\'inscription du demandeur',
  historyDocOtherId: "Autre pièce d'identité",
  historyDocAsylumDocuments: "Documents d'asile",
  historyDocCriminalRecordCertificate: 'Certificat de casier judiciaire',
  historyDocProofContinuousResidence: 'Preuve de résidence',
  historyCriminalRecordNote: 'Par pays, validité de 3 mois',
  historyEmployment: 'Emploi',
  historyVulnerabilityReport: 'Rapport de vulnérabilité',
  historyDocumentUploaded: 'Document téléversé - {{document}}',
  historyDocumentAccepted: 'Document accepté - {{document}}',
  historyAdvisorAssigned: 'Conseiller attribué',
  historyCaseOpened: 'Dossier ouvert',
  historyHelpRequest: 'Le demandeur a demandé de l\'aide',
  historyHelpReplied: 'A répondu à la question du demandeur',
  helpPending: 'En attente de réponse',
  helpResponded: 'Répondu',
  helpModalTitle: 'Question du demandeur',
  familyInterestModalTitle: 'Intérêt pour une demande familiale',
  historyFamilyApplicationInterest: 'Intérêt pour une demande familiale',
  historyFamilyInterestReplied: 'Réponse sur la demande familiale',
  historyFamilyInterestResponded: 'Intérêt familial marqué comme traité',
  helpModalSection: 'Section',
  helpModalClose: 'Fermer',
  helpReplyLabel: 'Votre réponse',
  helpReplyPlaceholder: 'Rédigez votre réponse…',
  helpReplyAndResolve: 'Répondre et résoudre',
  helpMarkRespondedBtn: 'Résoudre sans réponse',
  helpRespondingBtn: 'Enregistrement…',
  helpMarkedResponded: 'Marqué comme répondu - suivi supprimé',
  helpReplySent: 'Réponse envoyée - suivi supprimé',
  historyFileAccepted: '{{file}} a été accepté.',
  historyFileLabel: 'Fichier : {{file}}',
  historyCaseAssignedBody: 'Dossier attribué à {{name}}.',
  historyCaseOpenedBody:
    "Dossier de demande créé (UNIFIED). Le demandeur peut continuer dans le chat ou téléverser les documents via le portail.",
  downloadStarted: 'Téléchargement lancé - enregistrement de {{name}}. Vérifiez votre dossier Téléchargements.',
  packageNameFallback: 'dossier',
};

export const advisorPortalAr = {
  ...advisorPortalEn,
  breadcrumbBack: 'رجوع',
  breadcrumbApplications: 'الطلبات',
  generateForm: 'إنشاء النموذج',
  downloadPackage: 'تنزيل الحزمة',
  draftBanner: 'مسودة - لم يتم تقديم هذا الطلب بعد.',
  tabCaseDetails: 'التفاصيل',
  tabHistory: 'السجل',
  tabDocuments: 'المستندات',
  addNoteTitle: 'إضافة ملاحظة',
  recentHistory: 'السجل الأخير',
  viewAll: 'عرض الكل',
  noHistoryYet: 'لا يوجد سجل بعد.',
  activityFilterAll: 'الكل',
  activityFilterNotes: 'ملاحظات',
  activityFilterActivity: 'النشاط',
  historyNoEntriesMatch: 'لا توجد عناصر مطابقة لهذا الفلتر',
  sectionIdentity: 'الهوية',
  sectionFamily: 'العائلة',
  docCat_proof_of_residency_name: 'إثبات الإقامة',
  docCat_criminal_record_name: 'السجل الجنائي',
  docCat_other_name: 'أخرى',
  reviewAccepted: 'مقبول',
  reviewRejected: 'مرفوض',
  reviewPending: 'قيد الانتظار',
  sidebarUploadedN: '{{count}} مرفوع',
  activityEntryInternalNote: 'ملاحظة داخلية',
  activityEntryCallInbound: 'مكالمة هاتفية واردة',
  activityEntryCallOutbound: 'مكالمة هاتفية صادرة',
  activityEntryWhatsAppInbound: 'واتساب وارد',
  activityEntryWhatsAppOutbound: 'واتساب صادر',
  activityEntryEmailInbound: 'بريد إلكتروني وارد',
  activityEntryEmailOutbound: 'بريد إلكتروني صادر',
  activityEntrySmsInbound: 'رسالة SMS واردة',
  activityEntrySmsOutbound: 'رسالة SMS صادرة',
  activityTitleCallInbound: 'مكالمة واردة',
  activityTitleCallOutbound: 'مكالمة صادرة',
  activityTitleWhatsAppInbound: 'تم استلام رسالة واتساب',
  activityTitleWhatsAppOutbound: 'تم إرسال رسالة واتساب',
  activityTitleEmailInbound: 'تم استلام بريد إلكتروني',
  activityTitleEmailOutbound: 'تم إرسال بريد إلكتروني',
  activityTitleSmsInbound: 'تم استلام SMS',
  activityTitleSmsOutbound: 'تم إرسال SMS',
  activityEntryTypeAria: 'نوع الإدخال',
  activityDocTagAria: 'ربط مستند',
  activityDocTagTitle: 'ربط مستند',
  activityClickToWrite: 'انقر لكتابة ملاحظة...',
  activityWriteNotePlaceholder: 'اكتب ملاحظتك هنا...',
  activityNotePlaceholder: 'سجّل ملاحظة أو مكالمة أو رسالة…',
  activityPostPosting: 'جارٍ النشر…',
  activityPost: 'نشر',
  add: 'إضافة',
  mobileFieldVerify: 'تحقق',
  mobileFieldVerified: 'تم التحقق',
  remove: 'حذف',
  qualificationTitle: 'إجابات تسجيل مقدم الطلب',
  historyDocOtherId: 'هوية أخرى',
  historyDocAsylumDocuments: 'مستندات اللجوء',
  historyDocCriminalRecordCertificate: 'شهادة السجل الجنائي',
  historyDocProofContinuousResidence: 'إثبات الإقامة',
  historyCriminalRecordNote: 'لكل بلد، صلاحية 3 أشهر',
  historyEmployment: 'العمل',
  historyVulnerabilityReport: 'تقرير الهشاشة',
  historyDocumentUploaded: 'تم رفع مستند - {{document}}',
  historyDocumentAccepted: 'تم قبول المستند - {{document}}',
  historyAdvisorAssigned: 'تم تعيين المستشار',
  historyCaseOpened: 'تم فتح القضية',
  historyHelpRequest: 'طلب مقدم الطلب المساعدة',
  historyHelpReplied: 'تم الرد على سؤال مقدم الطلب',
  helpPending: 'في انتظار الرد',
  helpResponded: 'تم الرد',
  helpModalTitle: 'سؤال مقدم الطلب',
  familyInterestModalTitle: 'اهتمام بطلب عائلي',
  historyFamilyApplicationInterest: 'اهتمام بطلب عائلي',
  historyFamilyInterestReplied: 'رد بخصوص الطلب العائلي',
  historyFamilyInterestResponded: 'تم وضع علامة تم الرد على اهتمام الطلب العائلي',
  helpModalSection: 'القسم',
  helpModalClose: 'إغلاق',
  helpReplyLabel: 'ردك',
  helpReplyPlaceholder: 'اكتب ردك…',
  helpReplyAndResolve: 'الرد والحل',
  helpMarkRespondedBtn: 'حل بدون رد',
  helpRespondingBtn: 'جارٍ الحفظ…',
  helpMarkedResponded: 'تم وضع علامة تم الرد - تمت إزالة المتابعة',
  helpReplySent: 'تم إرسال الرد - تمت إزالة المتابعة',
  historyFileAccepted: 'تم قبول {{file}}.',
  historyFileLabel: 'الملف: {{file}}',
  historyCaseAssignedBody: 'تم تعيين القضية إلى {{name}}.',
  historyCaseOpenedBody:
    'تم إنشاء قضية الطلب (UNIFIED). يمكن لمقدم الطلب المتابعة في الدردشة أو إكمال رفع الملفات عبر البوابة.',
  downloadStarted: 'بدأ التنزيل - جارٍ حفظ {{name}}. تحقق من مجلد التنزيلات.',
  packageNameFallback: 'الحزمة',
};

export const advisorPortalZh = {
  ...advisorPortalEn,
  breadcrumbBack: '返回',
  breadcrumbApplications: '申请',
  generateForm: '生成表格',
  downloadPackage: '下载材料包',
  draftBanner: '草稿 - 此申请尚未提交。',
  tabCaseDetails: '详情',
  tabHistory: '历史',
  tabDocuments: '文件',
  addNoteTitle: '添加备注',
  recentHistory: '最近记录',
  viewAll: '查看全部',
  noHistoryYet: '暂无历史记录。',
  activityFilterAll: '全部',
  activityFilterNotes: '备注',
  activityFilterActivity: '活动',
  historyNoEntriesMatch: '没有符合此筛选条件的记录',
  sectionIdentity: '身份',
  sectionFamily: '家庭',
  docCat_proof_of_residency_name: '居住证明',
  docCat_criminal_record_name: '无犯罪记录',
  docCat_other_name: '其他',
  reviewAccepted: '已接受',
  reviewRejected: '已拒绝',
  reviewPending: '待处理',
  sidebarUploadedN: '已上传 {{count}} 份',
  activityEntryInternalNote: '内部备注',
  activityEntryCallInbound: '来电',
  activityEntryCallOutbound: '去电',
  activityEntryWhatsAppInbound: 'WhatsApp（收到）',
  activityEntryWhatsAppOutbound: 'WhatsApp（发送）',
  activityEntryEmailInbound: '电子邮件（收到）',
  activityEntryEmailOutbound: '电子邮件（发送）',
  activityEntrySmsInbound: '短信（收到）',
  activityEntrySmsOutbound: '短信（发送）',
  activityTitleCallInbound: '收到来电',
  activityTitleCallOutbound: '拨出电话',
  activityTitleWhatsAppInbound: '收到 WhatsApp',
  activityTitleWhatsAppOutbound: '发送 WhatsApp',
  activityTitleEmailInbound: '收到电子邮件',
  activityTitleEmailOutbound: '发送电子邮件',
  activityTitleSmsInbound: '收到短信',
  activityTitleSmsOutbound: '发送短信',
  activityEntryTypeAria: '记录类型',
  activityDocTagAria: '关联文件',
  activityDocTagTitle: '关联文件',
  activityClickToWrite: '点击写备注...',
  activityWriteNotePlaceholder: '在此输入备注...',
  activityNotePlaceholder: '记录备注、通话或消息…',
  activityPostPosting: '发布中…',
  activityPost: '发布',
  add: '添加',
  mobileFieldVerify: '验证',
  mobileFieldVerified: '已验证',
  remove: '移除',
  qualificationTitle: '申请人注册问答',
  historyDocOtherId: '其他身份证件',
  historyDocAsylumDocuments: '庇护文件',
  historyDocCriminalRecordCertificate: '无犯罪记录证明',
  historyDocProofContinuousResidence: '居住证明',
  historyCriminalRecordNote: '按国家计算，3 个月有效',
  historyEmployment: '就业',
  historyVulnerabilityReport: '脆弱性报告',
  historyDocumentUploaded: '文件已上传 - {{document}}',
  historyDocumentAccepted: '文件已接受 - {{document}}',
  historyAdvisorAssigned: '已分配顾问',
  historyCaseOpened: '案件已创建',
  historyHelpRequest: '申请人请求帮助',
  historyHelpReplied: '已回复申请人的问题',
  helpPending: '等待回复',
  helpResponded: '已回复',
  helpModalTitle: '申请人的问题',
  familyInterestModalTitle: '家庭申请意向',
  historyFamilyApplicationInterest: '家庭申请意向',
  historyFamilyInterestReplied: '已回复家庭申请意向',
  historyFamilyInterestResponded: '家庭申请意向已标记为已处理',
  helpModalSection: '部分',
  helpModalClose: '关闭',
  helpReplyLabel: '您的回复',
  helpReplyPlaceholder: '写下您的回复…',
  helpReplyAndResolve: '回复并解决',
  helpMarkRespondedBtn: '无回复解决',
  helpRespondingBtn: '保存中…',
  helpMarkedResponded: '已标记为已回复 - 跟进已清除',
  helpReplySent: '回复已发送 - 跟进已清除',
  historyFileAccepted: '{{file}} 已被接受。',
  historyFileLabel: '文件：{{file}}',
  historyCaseAssignedBody: '案件已分配给 {{name}}。',
  historyCaseOpenedBody:
    '申请案件已创建（UNIFIED）。申请人可以继续在聊天中操作，或通过门户完成上传。',
  downloadStarted: '下载已开始 - 正在保存 {{name}}。请查看“下载”文件夹。',
  packageNameFallback: '材料包',
};
